//#region Imports

import IntraoralViewer from "./IntraoralViewer.vue";
import IntraoralAddDialog from "./controls/IntraoralAddDialog.vue";
import IntraoralSaveDialog from "./controls/IntraoralSaveDialog.vue";
import IntraoralEditNumberDialog from "./controls/IntraoralEditNumberDialog.vue";
import IntraoralListPanel from "./controls/IntraoralListPanel.vue";
import IntraoralListItem from "./controls/IntraoralListItem.vue";
import Konva from "konva";
import { STLExporter } from "three/addons/exporters/STLExporter.js";
import { getPanoramicImgUrl } from "@/api/cloudApi";
import {
  measureMouseClick,
  measureMouseMove,
  measureMouseUp,
  measureMouseDown,
  addMeasureClick,
  switchMeasureMode,
} from "./measure/IntraoralMeasureMode";
import {
  switchArchMode,
  archMouseClick,
  archMouseMove,
  archMouseUp,
  archMouseDown,
} from "./measure/IntraoralArchMode";
import { saveBlob, saveFile } from "@/components/reports/fileMethods";
import swal from "sweetalert";

import {
  createGraph,
  getShortPath,
  getPointIndex,
  getVertices,
  getFaces,
} from "./GraphHelper";
import {
  drawTeethNumbers,
  removeTeethNumbers,
  showToothNumber,
} from "./number/IntraoralNumberMode";
import {
  getDistance,
  getDistance2D,
  getFacesGeometry,
  areNearPoints,
  clearTempMeshes,
  drawSpherePoint,
  drawBox,
  drawPoint,
  split_mesh_islands,
} from "./IntraoralDraw";
import {
  createIoPoint,
  updateIoPointScale,
  createTubeCurve,
  updateTubeCurve,
  updateTubeLine,
  updateBezierPoints,
} from "./IntraoralTube";
import {
  intraoralZip,
  ioUpperMatrix,
  ioUpperMatrixInverted,
  ioLowerMatrix,
  fetchJsonAsync,
  fetchZipAsync,
  loadMeasurementsFromZip,
  loadLower,
} from "./IntraoralLoader";
import { getColorFromToothNumber } from "./TeethColors";
import {
  sendSaveIntraoralDataRequest,
  sendApplyAnchorPointsRequest,
  sendApplyBorderPointsRequest,
  sendCreateStubsRequest,
  sendCreateMeasurementsRequest,
  saveMeasurements,
  sendRemoveBracesRequest,
  checkPatientInPostprocess,
  checkBracesTaskInProgress,
  sendSavePanoJsonRequest,
} from "@/api/intraoralApi";
import { degreesToRadians } from "@/components/geometry/geometry.js";
import {
  loadOrthoStage,
  downloadOrhoStages,
} from "@/components/ortho/OrthoPlanMode.js";

import {
  getGeometryFromStl,
  getMergedGeometry,
  getGMergedGeometry,
  exportMeshToStlBlob,
  exportGeometryToStlBlob,
  getFacesArea,
} from "@/components/geometry/geometryLoader.js";
import { hideLoader, delay } from "@/components/controls/Loader.vue";
import * as BufferGeometryUtils from "three/addons/utils/BufferGeometryUtils.js";

import {
  MeshBVH,
  acceleratedRaycast,
  computeBoundsTree,
  disposeBoundsTree,
  CONTAINED,
  INTERSECTED,
  NOT_INTERSECTED,
} from "three-mesh-bvh";

import { SUBTRACTION, Brush, Evaluator } from "three-bvh-csg";

// import { LineSegments2 } from "three/addons/lines/LineSegments2.js";
// import { LineSegmentsGeometry } from "three/addons/lines/LineSegmentsGeometry.js";
// import { LineMaterial } from "three/addons/lines/LineMaterial.js";

// THREE.Mesh.prototype.raycast = acceleratedRaycast;
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;

//#endregion

//#region Fields

let viewMode = {
  opacity: 1,
  isFrontView: false,
  isOriginalView: false,
  hasSmoothView: false,
  isSmoothView: false,
  isFilledTeethView: false,
  isPanoramicRoots: false,
  hasPanoramicRoots: false,
  isEdit: false,
  isMeasureMode: false,
  isArchMode: false,
  isBrushMode: false,
  isBrushDraw: true,
  brushSize: 5,
  isSaving: false,
  isPostprocessing: false,
  isUpper: true,
  isLower: false,
  isDouble: false,
  isLoaded: false,
  allowDownloads: true,
  showTeethNumbers: false,
  showPanoramicImg: true,
  panoMaxItems: [
    { id: 18, icon_color: "white" },
    { id: 17, icon_color: "white" },
    { id: 16, icon_color: "white" },
    { id: 15, icon_color: "white" },
    { id: 14, icon_color: "white" },
    { id: 13, icon_color: "white" },
    { id: 12, icon_color: "white" },
    { id: 11, icon_color: "white" },
    { id: 21, icon_color: "white" },
    { id: 22, icon_color: "white" },
    { id: 23, icon_color: "white" },
    { id: 24, icon_color: "white" },
    { id: 25, icon_color: "white" },
    { id: 26, icon_color: "white" },
    { id: 27, icon_color: "white" },
    { id: 28, icon_color: "white" },
  ],
  panoMandItems: [
    { id: 48, icon_color: "white" },
    { id: 47, icon_color: "white" },
    { id: 46, icon_color: "white" },
    { id: 45, icon_color: "white" },
    { id: 44, icon_color: "white" },
    { id: 43, icon_color: "white" },
    { id: 42, icon_color: "white" },
    { id: 41, icon_color: "white" },
    { id: 31, icon_color: "white" },
    { id: 32, icon_color: "white" },
    { id: 33, icon_color: "white" },
    { id: 34, icon_color: "white" },
    { id: 35, icon_color: "white" },
    { id: 36, icon_color: "white" },
    { id: 37, icon_color: "white" },
    { id: 38, icon_color: "white" },
  ],
  panoOverlayVisible: true,
};

const panoPanelItems = viewMode.panoMaxItems.concat(viewMode.panoMandItems);
const panoMaskFigures = [];
let panoJsonData = null;
let panoKonvaStage = null;
let panoKonvaLayer = null;
let selectedPanoFigure = null;

const csgEvaluator = new Evaluator();

const randomColorValue = "random";
const monochromeColorValue = "monochrome";
const greyColorValue = "#D0CECF";
const gingivaColor = "#ffb6c1";
let selectedTeethColor = "random";

const GEO_TAG = "geo_";
const EXTR_TAG = "extr_";
const CUT_TAG = "cut_";
const SMOOTHED_TAG = "smoothed_";
const TOOTH_TAG = "tooth_";
const STL_TAG = ".stl";

const sceneBackgroundColor = "#082F5E"; //"#77a7fd";
const pongMaterial = new THREE.MeshPhongMaterial({
  // color: gingivaColor,
  flatShading: false,
  // flatShading: true,
  specular: 0x050505,
  shininess: 200,
  side: 2, //THREE.FrontSide,
  polygonOffset: true,
  transparent: true,
  // polygonOffsetUnit: 1,
  // wireframe: false,
});

const addedSuffix = "added_";
const upperSuffix = "upper_";
const lowerSuffix = "lower_";
const globalDz = 0;
const cameraDistance = 30;
const camera_dz = 0;

let viewerInitialized = false;
let controlsInitialized = false;
let allModels = [];

let isDoubleMode = false;
let isUpperMode = false;
let isEditMode = false;
let editDataLoaded = false;
// let editDataRequested = false;
let rootStubsAttempted = false;
let rootStubsLoaded = false;
let extrTeethInitialized = false;
let extrTeethLoaded = false;
let panoramicRootsLoaded = false;
let isChangingToothMouseUp = false;

let ioViewer;
let ioPlane = null;
let ioRaycaster = new THREE.Raycaster(
  new THREE.Vector3(),
  new THREE.Vector3(),
  0.0001,
  100000,
);
let defaultIoBox = null;
let defaultIoZoom = null;
let defaultIoZoom2 = null;
let originalZoom = null;
let originalBox = null;
let originalCenter = null;

let selectedTooth = null;
let dblClickTooth = null;
let dblClickBorderGroup = null;
let taggedControl = null;

let mouseCameraPoint = null;
let downPoint = null;
let dragPoint = null;
let intersectedTooth = null;
let mdTime = null;

let teeth = [];
let gingivae = [];
let teethMeshes = [];
let upperMeshes = [];
let lowerMeshes = [];
let upperGingiva = null;
let lowerGingiva = null;

let upperMesh = null;
let upperBox = null;
let upperCameraCenter = null;
let upperGraph = null;
let lowerMesh = null;
let lowerBox = null;
let lowerCameraCenter = null;
let lowerGraph = null;
let upperCrossedPoints = [];
let lowerCrossedPoints = [];

let upper_measurements = null;
let lower_measurements = null;

let ioPanoMenu = null;
let ioPanoDeleteBtn = null;
let ioPanoChangeBtn = null;

let intraoralMenu = null;
let intraoralAddBtn = null;
let intraoralAddBorderBtn = null;
let intraoralAddMeasureBtn = null;
let intraoralDeleteBorderBtn = null;
let intraoralFocusBtn = null;
let intraoralDeleteBtn = null;
let intraoralChangeBtn = null;
let intraoralCenterBtn = null;
let intraoralFlipViewBtn = null;
let addToothGroup = null;
let addToothName = null;
let addToothNumber = null;

let ioChanges = [];
// let sampleChange = {
//   model: null,
//   oldGeometry: null,
//   oldPoints: null,
//   oldAnchorPoints: null,
//   oldAnchorGroup: null,
//   newGeometry: null,
//   newPoints: null,
//   newAnchorPoints: null,
//   newAnchorGroup: null,
// };
let currentIoChange = null;
let drawChange = null;

let allItems = [];
let editedItem = { listItem: { newNumber: 7 } };

let brushMesh;
let brushedFaces = new Map();

const minToothArea = 4;
let isBrushStartedOnTooth = false;
let isSmallAreaSelected = false;

//#endregion

//#region Init

function showIoViewer() {
  console.log("showIoViewer", new Date());
  vueApp.leaveModes(true, false, false);
  vueApp.isIntraoralView = true;
  viewMode.allowDownloads = !vueApp.isViewMode && vueApp.allowDownloads;
  viewMode.hasPanoramicRoots = vueApp.intraoralPanoramicZipUrl;

  if (!viewerInitialized) {
    showIntraoralLoader();
    const viewerInstance = new Vue({
      ...IntraoralViewer,
      propsData: {
        viewMode: viewMode,
      },
    });
    const vueContainer = document.createElement("div");
    document.getElementById("main_div").appendChild(vueContainer);
    viewerInstance.$mount(vueContainer);

    viewerInitialized = true;
    csgEvaluator.attributes = ["position", "normal"];
    csgEvaluator.useGroups = false;
  }

  initControls();

  if (vueApp.isViewMode) {
    isEditMode = false;
  }

  document.querySelector("#views_cont").style.display = "none";
  $(".menu3d").css({ display: "none" });
  // $(".menu3d").attr("disabled", true);

  document.querySelector("#intraoralRoot").style.display = "flex";
  if (ioViewer) ioViewer.do_resize();
}

function setAllModels(models) {
  allModels = models;
  console.log("setAllModels", allModels);
}

function initStlViewer() {
  console.log("initStlViewer", new Date());

  upperCrossedPoints = fillCrossedPoints(allModels.filter((m) => m.isUpper));
  lowerCrossedPoints = fillCrossedPoints(allModels.filter((m) => !m.isUpper));

  ioViewer = new StlViewer(document.getElementById("intraoralContainer"), {
    models: allModels,
    load_three_files: "js/",
    // center_models: false,
    controls: 1,
    // zoom: -1,
  });
  ioViewer.scene.background = new THREE.Color(sceneBackgroundColor);
  ioViewer.controls.zoomSpeed = 2.1;
  ioViewer.controls.noZoom = true;
  ioViewer.controls.keysEnabled = false;

  setTimeout(async () => {
    console.log("loadCutTeethFiles start", new Date());
    // await loadCutTeethFiles(allModels);
    await stlAllLoaded();
  }, 100);
}

function fillCrossedPoints(models) {
  if (!models || !models.length) return;

  // console.log("fillCrossedPoints start", new Date());

  let crossedPoints = [];
  return crossedPoints;
  let allVolumes = models
    .filter((m) => m.isTooth)
    .map((model) => {
      let mesh = model.mesh;
      if (!mesh.geometry.boundingBox) {
        mesh.geometry.computeBoundingBox();
      }
      let box = mesh.geometry.boundingBox;
      // box = offsetBox(box, 0, 0, 0);
      let vertices = getVertices(mesh);
      let volume = {
        box: box,
        model: model,
        vertices: vertices,
      };
      model.crossedPoints = [];
      model.crossedTeeth = [];
      return volume;
    });

  // allVolumes.forEach((volume) => {
  //   let distance = Infinity;
  //   for (let vl of allVolumes) {
  //     let dst = getDistance(volume.center, vl.center);
  //   }
  //   console.log(volume.model.name, volume);
  // });

  allVolumes.forEach((vl) => {
    allVolumes.forEach((nvl) => {
      if (nvl != vl && nvl.box.intersectsBox(vl.box)) {
        vl.vertices.forEach((v) => {
          nvl.vertices.forEach((nv) => {
            if (v.x == nv.x && v.y == nv.y && v.z == nv.z) {
              crossedPoints.push(v);
              vl.model.crossedPoints.push(v);
              // console.log(vl.model.name, nvl.model.name, v);
              // drawPoint(v, ioViewer.scene);
            }
          });
        });
        vl.model.crossedTeeth.push(nvl.model.name);
      }
    });
  });
  // crossedPoints = [...new Set(crossedPoints)];
  // console.log("fillCrossedPoints end", crossedPoints.length, new Date());
  return crossedPoints;
}

function applyBorderPoints(borderPoints, isBasePoints) {
  for (let prop in borderPoints) {
    let name = prop.replace("stl", "").replace(".", "");
    let model = getModelByName(name);
    let newPoints = borderPoints[prop];
    if (model) {
      if (isBasePoints) {
        model.basePoints = model.points = newPoints;
      } else if (newPoints && newPoints.length > 0) {
        model.savedPoints = model.points = newPoints;
      }
    }
  }
}

function applyAnchorPoints(anchorPoints, isBasePoints, isUpper) {
  // console.log("applyAnchorPoints", isBasePoints, anchorPoints);
  if (!anchorPoints) return;

  let anchorKeys = Object.keys(anchorPoints);
  if (anchorKeys.length == 0) return;

  for (let model of allModels) {
    let modelName = model.name;
    let modelPoints = anchorPoints[modelName];
    if (modelPoints) {
      let anchorGroup = [modelPoints];
      let i = 1;
      let borderName = `${modelName}-${i}`;
      while (anchorPoints[borderName]) {
        anchorGroup.push(anchorPoints[borderName]);
        i++;
        borderName = `${modelName}-${i}`;
      }

      if (anchorGroup.length > 1) {
        anchorGroup.sort((arr1, arr2) => arr2.length - arr1.length);
        // console.log(modelName, anchorGroup);
        modelPoints = anchorGroup[0];
      }

      if (isBasePoints) {
        model.baseAnchorPoints = model.anchorPoints = modelPoints;
        model.baseAnchorGroup = model.anchorGroup = anchorGroup;
      } else {
        model.savedAnchorPoints = model.anchorPoints = modelPoints;
        model.savedAnchorGroup = model.anchorGroup = anchorGroup;
        if (!model.baseAnchorPoints) {
          model.baseAnchorPoints = model.savedAnchorPoints;
          model.baseAnchorGroup = model.savedAnchorGroup;
        }
      }
    } else if (
      model.isTooth &&
      model.isUpper === isUpper &&
      !isBasePoints &&
      !model.isDeleted &&
      (model.isAdded || model.baseAnchorPoints)
    ) {
      console.log("DELETED in applyAnchorPoints", model);
      model.isDeleted = true;
    }
  }
}

function applyMeasurements(measurements, isBasePoints, isUpper) {
  // console.log("applyMeasurements", measurements, isBasePoints, isUpper);

  if (isUpper) upper_measurements = measurements;
  else lower_measurements = measurements;

  let tmeasurements = measurements.teeth_measurements;
  if (!tmeasurements) return;

  for (const key in tmeasurements) {
    let model = allModels.find((x) => x.number === +key);
    if (model && Object.prototype.hasOwnProperty.call(tmeasurements, key)) {
      let measureObj = tmeasurements[key];
      // console.log("applyMeasurements", key, measureObj, model);
      model.measureObj = measureObj;
      model.measurePoints =
        measureObj && measureObj.points && measureObj.points.length > 1
          ? measureObj.points
          : null;
      if (model.measurePoints) {
        let tmp = measureObj.points[0];
        model.measurePoint1 = new THREE.Vector3(tmp[0], tmp[1], tmp[2]);
        tmp = measureObj.points[1];
        model.measurePoint2 = new THREE.Vector3(tmp[0], tmp[1], tmp[2]);

        if (isBasePoints) {
          model.baseMeasurePoints = model.measurePoints;
        } else {
          model.savedMeasurePoints = model.measurePoints;
          if (!model.baseMeasurePoints) {
            model.baseMeasurePoints = model.savedMeasurePoints;
          }
        }
      }
    }
  }
}

async function stlAllLoaded() {
  console.log("start intraoral stlAllLoaded", new Date());
  console.time("intraoral stlAllLoaded");

  const brushGeometry = new THREE.SphereGeometry(
    viewMode.brushSize / 5,
    32,
    32,
  );
  const brushMaterial = new THREE.MeshStandardMaterial({
    color: 0xec407a,
    roughness: 0.75,
    metalness: 0,
    transparent: true,
    opacity: 0.5,
    premultipliedAlpha: true,
    emissive: 0xec407a,
    emissiveIntensity: 0.5,
  });

  brushMesh = new THREE.Mesh(brushGeometry, brushMaterial);
  brushMesh.visible = false;
  ioViewer.scene.add(brushMesh);

  $id("loader").style.display = "none";
  console.log(`stlAllLoaded allModels`, allModels.length);
  ioPlane = new THREE.Mesh(
    new THREE.PlaneBufferGeometry(500, 500, 8, 8),
    new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      opacity: 0.00001,
    }),
  );
  ioViewer.scene.add(ioPlane);
  ioViewer.controls.screenSpacePanning = true;
  ioViewer.controls.addEventListener("change", onCameraChange);
  ioViewer.renderer.outputEncoding = THREE.sRGBEncoding;
  // ioViewer.scene.add(new THREE.AxesHelper(80));

  let currentMin = { x: ioViewer.minx, y: ioViewer.miny, z: ioViewer.minz };
  let currentMax = { x: ioViewer.maxx, y: ioViewer.maxy, z: ioViewer.maxz };
  let currentBox = { min: currentMin, max: currentMax };
  defaultIoBox = convertToBox(currentBox);
  console.log("defaultIoBox", defaultIoBox);
  ioPlane.position.set(
    defaultIoBox.center.x,
    defaultIoBox.center.y,
    defaultIoBox.center.z,
  );

  upperGingiva = allModels.find((x) => x.isGingiva && x.isUpper);
  lowerGingiva = allModels.find((x) => x.isGingiva && !x.isUpper);

  allModels.forEach((model) => {
    let vmesh = model.mesh;
    vmesh.renderOrder = model.isGingiva ? 0 : 10 + model.id;
    vmesh.name = model.name;
    vmesh.material.flatShading = false;
    vmesh.material.color = new THREE.Color(model.color);
    model.uuid = vmesh.uuid;

    if (!model.isGingiva) {
      teeth.push(model);
      teethMeshes.push(vmesh);
    } else {
      gingivae.push(model);
      if (model.isUpper) upperGingiva = model;
      else lowerGingiva = model;
    }
    if (model.isUpper) upperMeshes.push(vmesh);
    else lowerMeshes.push(vmesh);

    let box = vmesh.geometry.boundingBox;
    box = offsetBox(box, 0, 0, 0);
    model.defaultBox = box;
    // vmesh.position.set(box.center.x, box.center.y, box.center.z);

    let geometry = prepareGeometry(vmesh.geometry, model.isGingiva);
    if (vmesh.geometry != geometry) {
      vmesh.geometry.dispose();
      vmesh.geometry = model.mainGeometry = geometry;
    }

    if (model.isGingiva && !vmesh.stages) {
      vmesh.stages = {};
      vmesh.stages[-1] = { geometry: vmesh.geometry.clone() };
    }
  });

  if (upperMeshes.length && upperGingiva) {
    upperMesh = createGlobalMesh(upperMeshes, upperGingiva);
    upperBox = offsetBox(upperMesh.geometry.boundingBox);
    upperCameraCenter = new THREE.Vector3(
      upperBox.center.x,
      upperBox.center.y,
      upperBox.center.z,
    );
    // upperGraph = createGraph(upperMesh);
  }
  if (lowerMeshes.length && lowerGingiva) {
    lowerMesh = createGlobalMesh(lowerMeshes, lowerGingiva);
    lowerBox = offsetBox(lowerMesh.geometry.boundingBox);
    lowerCameraCenter = new THREE.Vector3(
      lowerBox.center.x,
      lowerBox.center.y,
      lowerBox.center.z,
    );

    // lowerGraph = createGraph(lowerMesh);
  }

  allModels.forEach((model) => {
    model.filename = `${model.name}.stl`;
    if (model.isGingiva) {
      return;
    }

    let vmesh = model.mesh;
    if (model.savedGeometry) {
      vmesh.geometry.dispose();
      vmesh.geometry = model.mainGeometry = model.savedGeometry.clone();
      vmesh.geometry.computeBoundingBox();

      let box = vmesh.geometry.boundingBox;
      box = offsetBox(box, 0, 0, 0);
      model.defaultBox = box;
      model.defaultPosition = {
        x: 0, // box.center.x,
        y: 0, // box.center.y,
        z: 0, // box.center.z,
      };
      // vmesh.position.set(box.center.x, box.center.y, box.center.z);

      let geometry = prepareGeometry(vmesh.geometry, model.isGingiva);
      if (vmesh.geometry != geometry) {
        vmesh.geometry.dispose();
        vmesh.geometry = model.mainGeometry = geometry;
      }
    } else {
      console.log("not saved geometry", model);
    }
    model.savedGeometry = model.mainGeometry = vmesh.geometry; //.clone();
  });

  // drawBox(defaultIoBox, 0xFF0000, ioViewer.scene);
  let padding = -10;
  let w = (defaultIoBox.size.x + padding) / 2;
  let h = (defaultIoBox.size.y + padding) / 2;
  let fovX = ioViewer.camera.fov * ioViewer.camera.aspect;
  let fovY = ioViewer.camera.fov;
  let distanceX = w / Math.tan((Math.PI * fovX) / 360) + w;
  let distanceY = h / Math.tan((Math.PI * fovY) / 360) + w;
  defaultIoZoom = Math.max(distanceX, distanceY);

  padding = -30;
  w = (defaultIoBox.size.x + padding) / 2;
  h = (defaultIoBox.size.y + padding) / 2;
  fovX = ioViewer.camera.fov * ioViewer.camera.aspect;
  fovY = ioViewer.camera.fov;
  distanceX = w / Math.tan((Math.PI * fovX) / 360) + w;
  distanceY = h / Math.tan((Math.PI * fovY) / 360) + w;
  defaultIoZoom2 = Math.max(distanceX, distanceY);

  allModels.forEach((model) => {
    // ioViewer.set_opacity(model.id, 0.7);
    ioViewer.set_opacity(model.id, 1);
    // ioViewer.set_opacity(model.id, model.isTooth ? 0.2 : 1);
    model.mesh.visible = !model.isDeleted;
  });

  if (upperMeshes.length > 0) {
    await switchViewModes(true, false);
  } else {
    await switchViewModes(false, true);
  }

  // upperCrossedPoints.forEach(p => {
  //   drawPoint(p, ioViewer.scene);
  // });

  attachEvents();

  startPostprocessWatch();
  $("#orthoMenu").removeAttr("style");
  switchEditMode(false);

  fillIntraoralList();
  $(".intraoral-list-ctrl").css("display", "block");
  $(`#intraoralListPanel`).addClass("show");
  viewMode.isLoaded = true;

  $id("intraoralContainer").style.visibility = "visible";

  setTimeout(function () {
    loadOrtho();
  }, 100);
  // window.requestIdleCallback(requestLoadEditData);

  console.timeEnd("intraoral stlAllLoaded");
  console.log("end intraoral stlAllLoaded", new Date());

  let errorTeeth = teeth.filter(
    (t) => !t.isDeleted && t.mesh.geometry.isSmallArea,
  );
  if (errorTeeth.length > 0) {
    let teethNumbers = errorTeeth.map((t) => t.number).join(", ");
    let swalText =
      errorTeeth.length == 1
        ? `Segmentation issue detected on tooth ${errorTeeth[0].number}. Please use the paint brush segmentation tool to annotate this tooth.`
        : `Segmentation issue detected on teeth ${teethNumbers}. Please use the paint brush segmentation tool to annotate these teeth.`;
    swal({
      text: swalText,
    });
  }

  // await loadMarpe();
}

async function loadMarpe() {
  try {
    // const response = await fetch("demo/marpe.stl");
    const response = await fetch("demo/tooth_99.stl");
    const arrayBuffer = await response.arrayBuffer();
    let geometry = getGeometryFromStl(arrayBuffer, true);
    geometry.computeBoundingBox();
    console.log("marpe geometry", geometry);

    let material = pongMaterial.clone();
    material.color = new THREE.Color("#FFFFFF");
    let mesh = new THREE.Mesh(geometry, material);

    // let matrix2 = new THREE.Matrix4();
    // matrix2.makeRotationAxis(new THREE.Vector3(0, 0, 1), rotateY);
    // mesh.geometry.applyMatrix4(matrix2);

    // let matrix1 = new THREE.Matrix4();
    // matrix1.makeRotationAxis(new THREE.Vector3(1, 0, 0), rotateX);
    // mesh.geometry.applyMatrix4(matrix1);

    // mesh.geometry.center();

    // mesh.geometry.translate(
    //   upperBox.center.x,
    //   upperBox.center.y,
    //   upperBox.center.z + 3,
    // );

    // const stlExporter = new STLExporter();
    // const data = stlExporter.parse(mesh, { binary: true });
    // const blob = new Blob([data], { type: "application/octet-stream" });
    // saveBlob(blob, "tooth_99.stl");

    ioViewer.scene.add(mesh);
  } catch (error) {
    console.log("loadMarpe error", error);
  }
}

function prepareGeometry(geometry1, isGingiva) {
  let geometry = geometry1;
  if (geometry.index) geometry = geometry.toNonIndexed();
  if (geometry.attributes) {
    geometry.deleteAttribute("color");
    geometry.deleteAttribute("uv");
    geometry.deleteAttribute("normal");
    // geometry.computeVertexNormals();
    // geometry.computeBoundingBox();
    if (geometry.attributes.position)
      geometry.attributes.position.gpuType = 1015;
    if (geometry.attributes.normal) geometry.attributes.normal.gpuType = 1015;
  }
  // if (!isGingiva) {
  geometry = BufferGeometryUtils.mergeVertices(geometry);
  // }
  geometry.computeVertexNormals();

  // let geometry = new THREE.Geometry().fromBufferGeometry(geometry1);
  // if (!isGingiva) {
  //   geometry.mergeVertices();
  // }
  // geometry.computeVertexNormals();

  // geometry = new THREE.BufferGeometry().fromGeometry(geometry);
  // if (!isGingiva) {
  //   geometry.deleteAttribute("normal");
  //   geometry = BufferGeometryUtils.mergeVertices(geometry);
  // }

  geometry.computeBoundingBox();

  if (!isGingiva) {
    let facesArea = getFacesArea(geometry);
    if (facesArea <= minToothArea) {
      geometry.isSmallArea = true;
    }
  }
  return geometry;
}

function createGlobalMesh(meshes, gingivaModel) {
  ioViewer.set_opacity(gingivaModel.id, 1);
  gingivaModel.mainMesh.visible = false;

  if (!gingivaModel.filledMesh) {
    let geometry = getMergedGeometry(meshes);
    let material = pongMaterial.clone();
    material.color = new THREE.Color(gingivaColor);
    material.polygonOffsetUnit = 1;
    material.polygonOffsetFactor = 9;

    let mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.name = gingivaModel.name;
    gingivaModel.filledMesh = mesh;
  }

  ioViewer.scene.add(gingivaModel.filledMesh);
  gingivaModel.mesh = gingivaModel.filledMesh;

  let globalMesh = gingivaModel.filledMesh;
  let globalGeometry = globalMesh.geometry;
  globalGeometry = prepareGeometry(globalGeometry, true);
  // globalGeometry = BufferGeometryUtils.mergeVertices(globalGeometry, 0.01);
  globalGeometry.computeBoundingBox();
  globalMesh.geometry.dispose();
  globalMesh.geometry = globalGeometry;
  // const colorArray = new Uint8Array(
  //   globalGeometry.attributes.position.count * 3
  // );
  // colorArray.fill(255);
  // const colorAttr = new THREE.BufferAttribute(colorArray, 3, true);
  // colorAttr.setUsage(THREE.DynamicDrawUsage);
  // globalGeometry.setAttribute("color", colorAttr);
  // globalMesh.material.vertexColors = true;

  // globalMesh.geometry.computeBoundsTree();
  globalGeometry.boundsTree = new MeshBVH(globalGeometry);
  globalMesh.raycast = acceleratedRaycast;

  let highlightMesh = globalMesh.highlightMesh;
  if (!highlightMesh) {
    highlightMesh = new THREE.Mesh();
  } else {
    highlightMesh.geometry.dispose();
  }

  highlightMesh.geometry = globalGeometry.clone();
  highlightMesh.material = globalMesh.material.clone();
  highlightMesh.geometry.drawRange.count = 0;
  // highlightMesh.material = new THREE.MeshPhongMaterial({
  //   depthWrite: false,
  // });
  highlightMesh.material.color.set(0x00ff00);
  highlightMesh.material.polygonOffsetFactor = 1;

  highlightMesh.renderOrder = 1000;
  // const group = new THREE.Group();
  // group.add(highlightMesh);

  // highlightMesh
  // ioViewer.scene.add(highlightMesh);
  globalMesh.highlightMesh = highlightMesh;
  ioViewer.scene.add(globalMesh.highlightMesh);
  return globalMesh;
}

function initControls() {
  if (!controlsInitialized) {
    var ctrls = [
      IntraoralAddDialog,
      IntraoralSaveDialog,
      IntraoralEditNumberDialog,
      IntraoralListPanel,
    ];
    ctrls.forEach((component) => {
      const componentInstance = new Vue({
        ...component,
        propsData: { item: editedItem, allItems: allItems },
      });
      const vueContainer = document.createElement("div");
      document.getElementById("intraoralViewer").appendChild(vueContainer);
      componentInstance.$mount(vueContainer);
    });
    controlsInitialized = true;
  }
}

function loadOrtho() {
  // if (vueApp.orthoStages.length < 1) {
  vueApp.downloadingStages = true;
  $("#orthoMenu").css({ display: "none" });
  downloadOrhoStages((stages) => {
    vueApp.downloadingStages = false;
    $("#orthoMenu").removeAttr("style");
    if (stages && stages.length) {
      let sorted = stages.sort((a, b) => a.id - b.id);
      sorted.forEach((stage) => {
        vueApp.orthoStages.push(stage);
      });
    }
  }, true);
  // }
}

async function loadEditData() {
  if (!editDataLoaded) {
    editDataLoaded = true;
    showIntraoralLoader();
    await sleep(100);
    requestLoadEditData();
    // window.requestIdleCallback(requestLoadEditData);

    if (!isEditMode) {
      isEditMode = viewMode.isEdit = true;
    }
  }
}

async function sleep(msec) {
  return new Promise((resolve) => setTimeout(resolve, msec));
}

function requestLoadEditData(deadline) {
  console.log("requestLoadEditData", deadline);
  if (!upperGraph && upperMesh) upperGraph = createGraph(upperMesh);
  if (!lowerGraph && lowerMesh) lowerGraph = createGraph(lowerMesh);
  // upperCrossedPoints = fillCrossedPoints(allModels.filter((m) => m.isUpper));
  // lowerCrossedPoints = fillCrossedPoints(allModels.filter((m) => !m.isUpper));
  editDataLoaded =
    (!upperGingiva || upperGraph) && (!lowerGingiva || lowerGraph);
  hideLoader();
}

function showIntraoralLoader(text1, text2) {
  if (!text1 && !text2) {
    text1 = `Loading data... Please wait`;
    text2 = ``;
  }

  $id("loader").style.display = "block";
  // $id("loader").style.background = "gray";
  $id("loader").style.color = "white";
  // $id("loader").style.opacity = "0.7";
  $("#loaderText").html(text1);
  $("#loaderText2").html(text2);
  $("#loaderText3").html(``);
}

//#endregion

//#region Item/Model Actions

function fillIntraoralList() {
  // console.log("fillIntraoralList ");

  const intraoralList = document.getElementById("intraoralList");
  intraoralList.innerHTML = "";

  // const upperGingiva = allModels.find((x) => !x.isTooth && x.isUpper);
  addItemInList(allModels.find((x) => !x.isTooth && x.isUpper));
  addItemInList(allModels.find((x) => !x.isTooth && !x.isUpper));

  allModels
    .filter((x) => x.isTooth)
    .forEach((model) => {
      addItemInList(model);
    });
}

function addItemInList(model) {
  if (!model) return;

  const intraoralList = document.getElementById("intraoralList");
  let item = {
    id: model.id,
    uuid: model.uuid,
    name: model.name,
    number: model.number,
    newNumber: model.number,
    displayName: model.name,
    color: model.color,
    isTooth: model.isTooth,
    isVisible: true,
    isHovered: false,
    isClicked: false,
    isChanged: model.isChanged,
    isEditing: false,
    isFocused: false,
    isOriginal: false, //!model.isTooth,
    isOpen: false,
    isDeleted: model.isDeleted,
    isUpper: model.isUpper,
    defaultPosition: model.defaultPosition,
    defaultBox: model.defaultBox,
  };
  item.filename = `${model.name}.stl`;
  model.listItem = item;
  allItems.push(item);

  const itemInstance = new Vue({
    ...IntraoralListItem,
    propsData: {
      item: item,
      viewMode: viewMode,
    },
  });
  const vueContainer = document.createElement("div");
  intraoralList.appendChild(vueContainer);
  itemInstance.$mount(vueContainer);
}

async function changeItemVisibility(item) {
  if (!item) return;

  let model = getModelByName(item.name);
  if (model) {
    model.isVisible = item.isVisible;
  } else {
    return;
  }

  // if (model.isGingiva && model.isVisible && viewMode.isFilledTeethView) {
  //   await switchFilledTeethView(false);
  // }

  if (item.isUpper == isUpperMode) {
    if (!item.isVisible && item.isFocused) {
      item.isFocused = false;
      await switchItemFocus(item);
    }

    ioViewer.set_color(item.id, item.isVisible ? item.color : "transparent");

    if (model.mainMesh && model.mainMesh.numberGroup) {
      model.mainMesh.numberGroup.visible = item.isVisible;
    }

    if (model.pointsGroup && selectedTooth && model.id == selectedTooth.id) {
      model.pointsGroup.visible =
        !item.isFocused && item.isVisible && isEditMode;
    }
  }
  changeTeethColor(selectedTeethColor);
}

function deleteItem(item) {
  if (item) {
    let model = getModelByName(item.name, true);
    deleteModel(model);
  }
}

async function deleteModel(model) {
  if (model && (await checkIsEditMode(model))) {
    swal({
      text: "Are you sure you want to delete this tooth?",
      buttons: {
        yes: { text: "Yes", value: true },
        no: { text: "No", value: false },
      },
    }).then(async (willDelete) => {
      if (willDelete) {
        document.getElementById("selectedToothHint").innerHTML = "";

        if (model.listItem.isFocused) {
          model.listItem.isFocused = false;
          await switchItemFocus(model.listItem);
        }

        model.isDeleted = true;
        model.listItem.isDeleted = true;
        let index = allItems.indexOf(model.listItem);
        allItems.splice(index, 1);

        model.recentlyDeleted = true;
        model.mesh.visible = false;
        if (model.pointsGroup) {
          ioViewer.scene.remove(model.pointsGroup);
        }

        let mesh = model.mainMesh;
        if (mesh.numberGroup) {
          ioViewer.scene.remove(mesh.numberGroup);
          mesh.numberGroup = null;
          mesh.numberMesh = null;
        }
        model.pointsGroup = null;
        // ioViewer.scene.remove(model.mesh);
        // let index = teeth.indexOf(selectedTooth);
        // teeth.splice(index, 1);
        // teethMeshes.splice(index, 1);
        selectedTooth = null;
      }
    });
  }
}

async function editItemNumber(item) {
  if (item && (await checkIsEditMode(getModelByItem(item)))) {
    if (viewMode.isSaving || viewMode.isPostprocessing) {
      swal({
        text: "Cannot edit tooth settings while post processing.",
      });
      return;
    }

    item.newNumber = item.number;
    editedItem.listItem = item;
    $("#intraoralEditNumberDialog").modal();
  }
}

function changeToothNumber(item) {
  let oldNumber = item.number;
  let newNumber = item.newNumber;
  // let oldToothName = `tooth_${oldNumber}`;
  let newToothName = `tooth_${newNumber}`;
  let tooth = getModelByItem(item);
  if (tooth && !tooth.isDeleted) {
    tooth.name = item.name = item.displayName = newToothName;
    tooth.number = item.number = newNumber;
    updateSelectedTooth(tooth);

    if (tooth.mesh && viewMode.showTeethNumbers) showToothNumber(tooth);
  }
}

function highlightItem(item) {
  if (viewMode.isOriginalView) return;
  if (
    item &&
    item.isTooth &&
    item.isVisible &&
    !item.isHidden &&
    !item.isFocused &&
    (item.isUpper === isUpperMode || isDoubleMode) &&
    (!viewMode.isBrushMode || !selectedTooth || item != selectedTooth.listItem)
  ) {
    var mesh = ioViewer.get_model_mesh(item.id);
    if (mesh) {
      var rgb = mesh.material.color;
      if (rgb.r == 1 && rgb.g == 1 && rgb.b == 1) {
        mesh.material.color.setRGB(0.8, 0.8, 0.8);
      } else {
        var r = Math.min(1.0, rgb.r + 0.2);
        var g = Math.min(1.0, rgb.g + 0.2);
        var b = Math.min(1.0, rgb.b + 0.2);
        mesh.material.color.setRGB(r, g, b);
      }
    }
  }
  let number = item ? item.number : 0;
  let panoFigures = getPanoFiguresByNumber(number);
  for (const panoFigure of panoFigures) {
    if (panoFigure.panelItem) {
      panoFigure.panelItem.icon_color = "orange";
    }
    if (panoFigure.panoShape && viewMode.panoOverlayVisible) {
      panoFigure.panoShape.opacity(1);
    }
  }
}

function unhighlightItem(item) {
  if (viewMode.isOriginalView) return;
  if (
    item &&
    item.isTooth &&
    item.isVisible &&
    !item.isHidden &&
    !item.isFocused &&
    (item.isUpper === isUpperMode || isDoubleMode) &&
    (!viewMode.isBrushMode || !selectedTooth || item != selectedTooth.listItem)
  ) {
    let color =
      selectedTeethColor == randomColorValue ? item.color : selectedTeethColor;
    if (selectedTeethColor == monochromeColorValue) {
      color = greyColorValue;
    }
    ioViewer.set_color(item.id, color);
  }

  let number = item ? item.number : 0;
  let panoFigures = getPanoFiguresByNumber(number);
  for (const panoFigure of panoFigures) {
    let isFocused = panoFigure.item && panoFigure.item.isFocused;
    if (panoFigure.panelItem && !isFocused) {
      panoFigure.panelItem.icon_color = panoFigure.item ? "blue" : "white";
    }
    if (panoFigure.panoShape && viewMode.panoOverlayVisible) {
      panoFigure.panoShape.opacity(0.5);
    }
  }
}

async function switchItemFocus(item) {
  let model = getModelByItem(item);
  if (model) {
    let isFocused = item.isFocused;
    model.isFocused = isFocused;

    if (isFocused && isDoubleMode) {
      await leaveDoubleMode(model);
    }

    if (isFocused && viewMode.isOriginalView) {
      await switchOriginalView(false);
    }

    if (isFocused && isEditMode) {
      await switchEditMode(false);
    }
    if (isFocused) {
      await switchMeasureMode(false);
      await switchArchMode(false);
    }

    model.isFocused = isFocused;
  }

  allItems.forEach((x) => {
    if (x != item) x.isFocused = false;
  });

  if (item && item.isFocused) {
    item.isHidden = false;
    item.isVisible = true;
    if (model.isUpper != isUpperMode) {
      isUpperMode = !isUpperMode;
      viewMode.isUpper = isUpperMode;
      viewMode.isLower = !isUpperMode;
      await switchViewModes(viewMode.isUpper, viewMode.isLower);
    }

    model.mesh.visible = model.isVisible =
      model.isUpper == isUpperMode && item.isVisible;

    if (model.pointsGroup) {
      //  && model.mesh.visible && model == selectedTooth
      model.pointsGroup.visible = false;
    }

    if (model.isGingiva) {
      // resetIoCamera();
      // if (isUpperMode) rotateIntraoralToView(2);
      model.mainMesh.visible = true;
      model.filledMesh.visible = false;
      model.mesh = model.mainMesh;
    } else {
      centerViewClick(model);
    }
  } else {
    if (!model || model.isTooth) {
      resetIoCamera();
      if (isUpperMode) rotateIntraoralToView(2);
    }
    selectedTooth = null;
  }

  allItems.forEach((x) => {
    if (x.isDeleted) return;
    let xmodel = getModelByItem(x);
    if (x != item) {
      x.isFocused = false;
      x.isHidden = !!item && item.isFocused;
      if (xmodel) {
        xmodel.isFocused = false;
      }
    }
    if (xmodel) {
      if (xmodel.pointsGroup) {
        xmodel.pointsGroup.visible = false;
      }
      if (xmodel.mainMesh && xmodel.mainMesh.numberGroup) {
        xmodel.mainMesh.numberGroup.visible = false;
      }
      if (xmodel.isGingiva && !x.isFocused) {
        xmodel.mainMesh.visible = false;
        xmodel.filledMesh.visible = true;
        xmodel.mesh = xmodel.filledMesh;
      }
      xmodel.mesh.visible =
        xmodel.isUpper == isUpperMode && !x.isHidden && x.isVisible;
    }
  });

  for (const figure of panoMaskFigures) {
    let anyFocused = item && item.isFocused;
    let visible = figure.item == item || !anyFocused;
    if (figure.panoShape) {
      figure.panoShape.visible(visible);
    }
    if (figure.panoText) {
      figure.panoText.visible(visible);
    }
  }

  for (const panelItem of panoPanelItems) {
    panelItem.icon_color =
      panelItem.item == item && item.isFocused
        ? "orange"
        : panelItem.item
        ? "blue"
        : "white";
  }
}

async function switchItemOpen(item) {
  let xmodel = getModelByItem(item);
  if (xmodel && intraoralZip) {
    if (!xmodel.filledMesh.openedGeometry) {
      showIntraoralLoader();
      let openedFile = item.isUpper
        ? "upper_extr_mesh.stl"
        : "lower_extr_mesh.stl";
      let zipFile = intraoralZip.file(openedFile);
      let buffer = await zipFile.async("arraybuffer");
      let geometry = getGeometryFromStl(buffer);
      geometry.computeBoundingBox();
      xmodel.filledMesh.closedGeometry = xmodel.filledMesh.geometry;
      xmodel.filledMesh.openedGeometry = geometry;
      hideLoader();
    }

    xmodel.filledMesh.geometry = item.isOpen
      ? xmodel.filledMesh.openedGeometry
      : xmodel.filledMesh.closedGeometry;
  }
}

async function selectItem(item) {
  console.log("selectItem itemDblClick", item.name);
  if (await checkIsEditMode(getModelByItem(item))) {
    await unfocusAny();

    let tooth = item.isTooth ? getModelByItem(item) : null;
    if (tooth) {
      if (tooth.isUpper != isUpperMode) {
        isUpperMode = !isUpperMode;
        viewMode.isUpper = isUpperMode;
        viewMode.isLower = !isUpperMode;
        await switchViewModes(viewMode.isUpper, viewMode.isLower);
        await switchToContourMode();
      }
    }
    updateSelectedTooth(tooth);
  }
}

async function unfocusAny() {
  let anyFocused = isAnyModelFocused();
  if (anyFocused) {
    let focusedItem = anyFocused.listItem;
    if (focusedItem) {
      focusedItem.isFocused = false;
      await switchItemFocus(focusedItem);
    }
  }
}

async function leaveDoubleMode(model) {
  if (isDoubleMode && model) {
    viewMode.isFrontView = false;
    viewMode.isUpper = model.isUpper;
    viewMode.isLower = !model.isUpper;
    await switchViewModes(model.isUpper, !model.isUpper);
  }
}

//#endregion

//#region Events

function attachEvents() {
  ioPanoMenu = document.getElementById("iopano-menu");
  document.getElementById("intraoralRoot").appendChild(ioPanoMenu);

  ioPanoChangeBtn = document.getElementById("iopano-change-btn");
  ioPanoChangeBtn.addEventListener("click", () => {
    console.log("ioPanoChangeBtn click");
    ioPanoMenu.style.display = "none";
    if (selectedPanoFigure) {
      selectedPanoFigure.newNumber = selectedPanoFigure.number;
      editedItem.listItem = selectedPanoFigure;
      $("#intraoralEditNumberDialog").modal();
      selectedPanoFigure = null;
    }
  });

  ioPanoDeleteBtn = document.getElementById("iopano-delete-btn");
  ioPanoDeleteBtn.addEventListener("click", async () => {
    console.log("ioPanoDeleteBtn click");
    ioPanoMenu.style.display = "none";
    if (selectedPanoFigure) {
      let willDelete = await swal({
        text: "Are you sure you want to delete this tooth?",
        buttons: {
          yes: { text: "Yes", value: true },
          no: { text: "No", value: false },
        },
      });

      if (willDelete) {
        let index = panoMaskFigures.indexOf(selectedPanoFigure);
        if (index > -1) {
          panoMaskFigures.splice(index, 1);
        }
        if (selectedPanoFigure.panoShape)
          selectedPanoFigure.panoShape.destroy();
        if (selectedPanoFigure.panoText) selectedPanoFigure.panoText.destroy();
        panoKonvaLayer.draw();
        selectedPanoFigure.panoShape =
          selectedPanoFigure.panoText =
          selectedPanoFigure.panelItem =
            null;

        index = panoJsonData.instance_segmentation.instances.indexOf(
          selectedPanoFigure.instance,
        );
        if (index > -1) {
          panoJsonData.instance_segmentation.instances.splice(index, 1);
          sendSavePanoJsonRequest(panoJsonData);
        }
      }

      selectedPanoFigure = null;
    }
  });

  intraoralMenu = document.getElementById("intraoral-tooth-menu");
  document.getElementById("intraoralViewer").appendChild(intraoralMenu);

  intraoralAddBtn = document.getElementById("intraoral-add-btn");
  intraoralAddBtn.addEventListener("click", () => {
    addClick();
  });

  intraoralAddBorderBtn = document.getElementById("intraoral-add-border-btn");
  intraoralAddBorderBtn.addEventListener("click", () => {
    addBorderClick();
  });

  intraoralAddMeasureBtn = document.getElementById("intraoral-add-measure-btn");
  intraoralAddMeasureBtn.addEventListener("click", () => {
    intraoralMenu.style.display = "none";
    addMeasureClick(dblClickTooth);
  });
  intraoralDeleteBorderBtn = document.getElementById(
    "intraoral-delete-border-btn",
  );
  intraoralDeleteBorderBtn.addEventListener("click", () => {
    deleteBorderClick();
  });

  intraoralFocusBtn = document.getElementById("intraoral-focus-btn");
  intraoralFocusBtn.addEventListener("click", () => {
    focusClick();
  });

  intraoralDeleteBtn = document.getElementById("intraoral-delete-btn");
  intraoralDeleteBtn.addEventListener("click", () => {
    deleteClick();
  });

  intraoralChangeBtn = document.getElementById("intraoral-change-btn");
  intraoralChangeBtn.addEventListener("click", () => {
    changeClick();
  });

  intraoralCenterBtn = document.getElementById("intraoral-center-btn");
  intraoralCenterBtn.addEventListener("click", () => {
    centerViewClick();
  });

  intraoralFlipViewBtn = document.getElementById("intraoral-flip-btn");
  intraoralFlipViewBtn.addEventListener("click", () => {
    flipViewClick();
  });

  let intraoralContainer = document.getElementById("intraoralContainer");
  intraoralContainer = [...intraoralContainer.children].find(
    (c) => c.tagName.toLowerCase() == "canvas",
  );
  let is_touch_enabled =
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0;
  if (is_touch_enabled) {
    intraoralContainer.addEventListener("touchstart", function (e) {
      var event = e.targetTouches[0];
      onDocumentMouseDown(event);
    });
    intraoralContainer.addEventListener("touchmove", function (e) {
      var event = e.targetTouches[0];
      onDocumentMouseMove(event);
    });
    intraoralContainer.addEventListener("touchend", function (e) {
      var event = e.changedTouches[0];
      onDocumentMouseUp(event);
    });
  } else {
    intraoralContainer.addEventListener(
      "mousedown",
      onDocumentMouseDown,
      false,
    );
    intraoralContainer.addEventListener(
      "mousemove",
      onDocumentMouseMove,
      false,
    );
    intraoralContainer.addEventListener("mouseup", onDocumentMouseUp, false);
    intraoralContainer.addEventListener("dblclick", onDocumentDblClick, false);
    intraoralContainer.addEventListener("click", onDocumentClick, false);
    intraoralContainer.addEventListener("wheel", onDocumentWheel, false);
    intraoralContainer.oncontextmenu = function (e) {
      stopEvent(e, true);
    };

    document.onkeydown = function (e) {
      if (vueApp.isIntraoralView && e.ctrlKey) {
        stopEvent(e, true);
        if (e.key == "z") {
          intraoralUndo();
        }
        if (e.key == "y") {
          intraoralRedo();
        }
      }
    };
  }
}

async function addClick() {
  intraoralMenu.style.display = "none";
  if (await checkIsEditMode(selectedTooth)) {
    $("#intraoralAddDialog").modal();
  }
}

async function addBorderClick() {
  intraoralMenu.style.display = "none";
  console.log("addBorderClick");
  if ((await checkIsEditMode(selectedTooth)) && selectedTooth) {
    $(".intraoral-add-item").css("display", "block");
    addToothGroup = new THREE.Group();
    selectedTooth.pointsGroup.add(addToothGroup);
  }
}

async function deleteBorderClick() {
  intraoralMenu.style.display = "none";
  if (
    (await checkIsEditMode(selectedTooth)) &&
    selectedTooth &&
    dblClickBorderGroup
  ) {
    let children = selectedTooth.pointsGroup.children;
    let length = children.length;
    let groupIndex = children.indexOf(dblClickBorderGroup);
    console.log("deleteBorderClick", groupIndex);
    if (length == 1) {
      swal({
        text: "It's the only tooth border. Do you want to delete this tooth?",
        buttons: {
          yes: { text: "Yes", value: true },
          no: { text: "No", value: false },
        },
      }).then(async (willDelete) => {
        if (willDelete) {
          deleteModel(selectedTooth);
        }
      });
    } else {
      swal({
        text: "Are you sure you want to delete this border?",
        buttons: {
          yes: { text: "Yes", value: true },
          no: { text: "No", value: false },
        },
      }).then(async (willDelete) => {
        if (willDelete) {
          selectedTooth.pointsGroup.remove(dblClickBorderGroup);
          setToothIsChanged(selectedTooth, true);
        }
      });
    }
  }
}

async function focusClick() {
  intraoralMenu.style.display = "none";
  if (dblClickTooth && dblClickTooth.listItem) {
    dblClickTooth.isFocused = dblClickTooth.listItem.isFocused =
      !dblClickTooth.listItem.isFocused;

    await switchItemFocus(dblClickTooth.listItem);
  }
}

function deleteClick() {
  intraoralMenu.style.display = "none";
  if (selectedTooth) {
    deleteModel(selectedTooth);
  }
}

function changeClick() {
  intraoralMenu.style.display = "none";
  if (selectedTooth) {
    editItemNumber(selectedTooth.listItem);
  }
}

function centerViewClick(tooth) {
  intraoralMenu.style.display = "none";
  if (!tooth) {
    tooth = dblClickTooth;
  }
  if (!tooth) {
    tooth = selectedTooth;
  }
  if (tooth) {
    tooth.isCenterView = true;
    tooth.isBacksideView = false;

    setToothCameraPoints(tooth);

    if (tooth.centerCameraPoint) {
      let box = tooth.defaultBox;

      rotateIntraoralToView(3, !tooth.isFocused);

      ioViewer.camera.position.copy(tooth.centerCameraPoint);

      ioViewer.controls.target.copy(
        new THREE.Vector3(box.center.x, box.center.y, box.center.z),
      );
    }
  }
}

function flipViewClick(tooth) {
  intraoralMenu.style.display = "none";
  if (!tooth) {
    tooth = dblClickTooth;
  }
  if (!tooth) {
    tooth = selectedTooth;
  }

  if (tooth) {
    if (tooth.isBacksideView) {
      centerViewClick();
      return;
    }

    tooth.isBacksideView = true;
    tooth.isCenterView = false;
    setToothCameraPoints(tooth);

    if (tooth.backsideCameraPoint) {
      rotateIntraoralToView(3);

      ioViewer.camera.position.copy(tooth.backsideCameraPoint);

      let box = tooth.defaultBox;
      ioViewer.controls.target.copy(
        new THREE.Vector3(box.center.x, box.center.y, box.center.z),
      );
    }
  }
}

function setToothCameraPoints(tooth) {
  if (tooth && !tooth.centerCameraPoint) {
    let mesh = ioViewer.get_model_mesh(tooth.id);
    if (mesh) {
      // clearTempMeshes(ioViewer.scene);
      if (!tooth.defaultBox) {
        mesh.geometry.computeBoundingBox();
        tooth.defaultBox = offsetBox(mesh.geometry.boundingBox);
      }

      let tootBoxCenter = tooth.defaultBox.center;
      let globalCenter = tooth.isUpper ? upperCameraCenter : lowerCameraCenter;

      let centersDistance = calculateDistance(tootBoxCenter, globalCenter);
      if (!tooth.centerCameraPoint) {
        let coef = (centersDistance + cameraDistance) / centersDistance;

        let px = (tootBoxCenter.x - globalCenter.x) * coef + globalCenter.x;
        let py = (tootBoxCenter.y - globalCenter.y) * coef + globalCenter.y;
        let pz = tootBoxCenter.z; // (tootBoxCenter.z - center.z) * coef + globalCenter.z;
        tooth.centerCameraPoint = new THREE.Vector3(px, py, pz);
        // drawSpherePoint(ioViewer.scene, tooth.centerCameraPoint, 0xff0000, 1);
        // drawLine(tootBoxCenter, tooth.centerCameraPoint,  null, ioViewer.scene);
      }

      if (!tooth.backsideCameraPoint) {
        let coef = (centersDistance - cameraDistance) / centersDistance;
        let px = (tootBoxCenter.x - globalCenter.x) * coef + globalCenter.x;
        let py = (tootBoxCenter.y - globalCenter.y) * coef + globalCenter.y;
        let pz = tootBoxCenter.z + tooth.defaultBox.size.z;
        tooth.backsideCameraPoint = new THREE.Vector3(px, py, pz);
      }
    }
  }
}

function onDocumentWheel(event) {
  updateRaycasterFromEvent(event);
  mouseCameraPoint = intersectMesh(getGlobalMesh());
  if (!mouseCameraPoint) {
    onCameraChange();
    mouseCameraPoint = intersectMesh(ioPlane);
  }

  var factor = 5;

  let intraoralContainer = document.getElementById("intraoralContainer");
  let overlayRect = intraoralContainer.getBoundingClientRect();

  var mX = (event.clientX / overlayRect.width) * 2 - 1;
  var mY = -(event.clientY / overlayRect.height) * 2 + 1;
  var vector = new THREE.Vector3(mX, mY, 0.1);

  let trackBallControls = ioViewer.controls;
  // drawPoint(trackBallControls.target, ioViewer.scene, 0x00ff00, 2);
  let camera = ioViewer.camera;

  vector.unproject(camera);
  vector.sub(camera.position);
  if (event.deltaY < 0) {
    camera.position.addVectors(camera.position, vector.setLength(factor));
    trackBallControls.target.addVectors(
      trackBallControls.target,
      vector.setLength(factor),
    );
  } else {
    camera.position.subVectors(camera.position, vector.setLength(factor));
    trackBallControls.target.subVectors(
      trackBallControls.target,
      vector.setLength(factor),
    );
  }
  // drawPoint(trackBallControls.target, ioViewer.scene, 0xff0000, 2);
  if (selectedTooth && selectedTooth.defaultBox) {
    if (isEditMode && viewMode.isBrushMode) {
      let globalMesh = getGlobalMesh();
      let toothCameraDistance = getDistance(
        ioViewer.camera.position,
        selectedTooth.defaultBox.center,
      );

      if (globalMesh && globalMesh.borderMesh) {
        globalMesh.borderMesh.children.forEach((line) => {
          updateTubeCurve(line, line.points, toothCameraDistance);
        });
      }
    }

    // console.log("onDocumentWheel toothCameraDistance", toothCameraDistance);
    let group = selectedTooth.pointsGroup;
    if (group && group.visible) {
      updatePointsGroupVisibility(selectedTooth, true);
    }
  }
}

function onDocumentClick(event) {
  if (viewMode.isMeasureMode) {
    measureMouseClick(event);
    return;
  }
  if (viewMode.isArchMode) {
    archMouseClick(event);
    return;
  }
}

function onDocumentDblClick(event) {
  updateRaycasterFromEvent(event);
  let isFocused = isAnyToothFocused();

  let dblClickPointsGroupMesh = null;
  if (selectedTooth && selectedTooth.pointsGroup) {
    dblClickPointsGroupMesh = intersectPointsGroup(selectedTooth.pointsGroup);
  }
  dblClickBorderGroup = dblClickPointsGroupMesh
    ? dblClickPointsGroupMesh.parent
    : null;

  dblClickTooth = dblClickPointsGroupMesh ? selectedTooth : intersectTooth();
  console.log("onDocumentDblClick", dblClickBorderGroup, dblClickTooth);

  intraoralFocusBtn.innerHTML =
    dblClickTooth && isFocused ? "Unfocus Tooth" : "Focus on Tooth";

  // intraoralAddBtn.style.display = !dblClickTooth ? "initial" : "none";

  intraoralDeleteBorderBtn.style.display =
    dblClickPointsGroupMesh && !isFocused && !viewMode.isBrushMode
      ? "initial"
      : "none";

  intraoralAddBorderBtn.style.display =
    dblClickTooth && !isFocused && !viewMode.isBrushMode ? "initial" : "none";

  intraoralAddMeasureBtn.style.display =
    dblClickTooth && viewMode.isMeasureMode && !dblClickTooth.measurePoints
      ? "initial"
      : "none";

  intraoralFocusBtn.style.display =
    intraoralDeleteBtn.style.display =
    intraoralChangeBtn.style.display =
    intraoralCenterBtn.style.display =
    intraoralFlipViewBtn.style.display =
      dblClickTooth ? "initial" : "none";

  if (!isEditMode) {
    intraoralDeleteBtn.style.display =
      intraoralChangeBtn.style.display =
      intraoralDeleteBorderBtn.style.display =
      intraoralAddBorderBtn.style.display =
        "none";
  }

  intraoralAddBtn.style.display = isEditMode && !isFocused ? "initial" : "none";

  let firstVisibleNode = [...intraoralMenu.childNodes].find(
    (x) => x.style.display != "none",
  );
  if (firstVisibleNode) {
    firstVisibleNode.style.borderTop = "0";
  }
  [...intraoralMenu.childNodes]
    .filter((x) => x != firstVisibleNode && x.style.display != "none")
    .forEach((x) => {
      x.style.borderTop = "1px solid #D9E6FE";
    });

  intraoralMenu.style.display = "initial";
  intraoralMenu.style.left = event.offsetX + 1 + "px";
  intraoralMenu.style.top = event.offsetY + 1 + "px";

  // updateRaycaster(event);
  // let point = intersectScene();
  // if (point) {
  //   let canvasHalfWidth = ioViewer.renderer.domElement.offsetWidth / 2;
  //   let canvasHalfHeight = ioViewer.renderer.domElement.offsetHeight / 2;

  //   let canvasPoint = point.project(ioViewer.camera);
  //   canvasPoint.x = canvasPoint.x * canvasHalfWidth + canvasHalfWidth;
  //   canvasPoint.y = -(canvasPoint.y * canvasHalfHeight) + canvasHalfHeight;
  // }
}

function onDocumentMouseDown(event) {
  if (viewMode.isMeasureMode) {
    measureMouseDown(event);
    return;
  }

  if (viewMode.isArchMode) {
    archMouseDown(event);
    return;
  }

  stopEvent(event);
  downPoint = { x: event.clientX, y: event.clientY };

  intraoralMenu.style.display = "none";

  mdTime = new Date();

  // console.log("onDocumentMouseDown", event.buttons);
  if (
    vueApp.activeMode &&
    vueApp.activeMode.isActive &&
    vueApp.activeMode.tagDetected
  ) {
    if (event.button && event.button > 0) {
      return;
    }

    updateRaycasterFromEvent(event);
    var selectedId = vueApp.activeMode.selectedId;
    if (selectedId) {
      const isShift = event.shiftKey;
      // console.log("isShift", isShift);
      var tagged = findTaggedControl("group", false, isShift);
      if (tagged) return true;
    }
  }

  if (!isEditMode && !addToothGroup) return;

  updateRaycasterFromEvent(event);

  if (viewMode.isEdit && viewMode.isBrushMode && selectedTooth) {
    if (!drawChange && teeth.includes(selectedTooth)) {
      let selectedMesh = ioViewer.get_model_mesh(selectedTooth.id);
      let oldAllPoints = getAllBorderPointsArr(selectedTooth);
      let oldAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
      let oldAnchorGroup = getSavedAnchorGroup(selectedTooth);
      drawChange = {
        model: selectedTooth,
        oldGeometry: selectedMesh.geometry,
        oldPoints: oldAllPoints,
        oldAnchorPoints: oldAnchorPoints,
        oldAnchorGroup: oldAnchorGroup,
      };
    }
    // let selectedMesh = selectedTooth.mesh; //ioViewer.get_model_mesh(selectedTooth.id);
    let globalMesh = getGlobalMesh();
    let intersectPoint = intersectMesh(globalMesh.highlightMesh);
    isBrushStartedOnTooth = !!intersectPoint;
    isSmallAreaSelected = selectedTooth.mesh.geometry.isSmallArea;
    // updateBrush(event);
    return;
  }

  if (selectedTooth && !addToothGroup) {
    let point = intersectPoint(selectedTooth.pointsGroup);
    if (point) {
      allItems.forEach((x) => {
        x.isHovered = false;
      });
      startDragPoint(point, event);
      return;
    }
  }

  if (event.button === 0) {
    intersectedTooth = intersectTooth();
  }
}

function findTaggedControl(tagParam = "group", isHighlight, isShift) {
  // need precall -  updateRaycaster(event);
  var combinedArray = [];
  ioViewer.scene.children.forEach((element) => {
    if (element.tag == tagParam) {
      combinedArray.push(...element.children);
    }
  });
  var intersects = ioRaycaster.intersectObjects(combinedArray);
  var intersectPoint = null;
  var tagged = intersects.find((intersect) => {
    intersectPoint = intersect.point;
    return intersect.object.tag != undefined;
  });

  if (taggedControl != tagged) {
    combinedArray.forEach((child) => {
      if (child.defaultColor) {
        child.material.color = new THREE.Color(child.defaultColor);
      }
    });
    document.body.style.cursor = "default";

    if (tagged) {
      document.body.style.cursor = "pointer";
      tagged.object.material.color = new THREE.Color(0xffff00);
    }
    taggedControl = tagged;
  }

  if (tagged && !isHighlight && vueApp.activeMode.tagDetected)
    vueApp.activeMode.tagDetected(
      tagged.object.tag,
      tagged.object,
      isShift,
      intersectPoint,
    );
  return tagged;
}

function onDocumentMouseMove(event) {
  if (viewMode.isMeasureMode) {
    measureMouseMove(event);
    return;
  }

  if (viewMode.isArchMode) {
    archMouseMove(event);
    return;
  }

  if (
    vueApp.activeMode &&
    vueApp.activeMode.isActive &&
    vueApp.activeMode.mouseMove
  ) {
    let needHighlight = vueApp.activeMode.mouseMove(event);
    if (!needHighlight) {
      return;
    }
    if (vueApp.activeMode.tagDetected) {
      updateRaycasterFromEvent(event);
      findTaggedControl("group", true);
    }
  }

  if (viewMode.isEdit && viewMode.isBrushMode && selectedTooth) {
    updateBrush(event);
    return;
  }

  // console.log("onDocumentMouseMove", event);
  if (dragPoint) {
    moveDragPoint(event);
  } else if (!event.buttons) {
    updateRaycasterFromEvent(event);

    var tooth = intersectTooth();
    if (tooth) {
      let item = tooth.listItem;
      if (item && !item.isHovered) {
        allItems.forEach((x) => {
          if (item != x) {
            x.isHovered = false;
          }
        });
        item.isHovered = true;
        // highlightItem(item);
      }
    } else {
      allItems.forEach((x) => {
        x.isHovered = false;
      });
    }
  }
}

function onDocumentMouseUp(event) {
  if (event.button == 2) {
    const element = document.querySelector("#main_div");
    const rect = element.getBoundingClientRect();
    let devent = { pageX: rect.width / 2, pageY: rect.height / 2 };
    updateRaycasterFromEvent(devent);
    mouseCameraPoint = intersectMesh(getGlobalMesh());
    if (!mouseCameraPoint) {
      onCameraChange();
      mouseCameraPoint = intersectMesh(ioPlane);
    }
    // setRotateCenter();
  }

  if (viewMode.isMeasureMode) {
    measureMouseUp(event);
    return;
  }

  if (viewMode.isArchMode) {
    archMouseUp(event);
    return;
  }

  if (viewMode.isEdit && viewMode.isBrushMode && selectedTooth) {
    updateGlobalBorderMesh();
    ioViewer.controls.enabled = true;
    // completeBrush(true);
    return;
    // brushMesh.visible = false;
  }
  // console.log("onDocumentMouseUp");
  if (vueApp.activeMode && vueApp.activeMode.isActive) {
    if (event.button && event.button > 0) {
      return;
    }
    let needSelection = true;
    if (vueApp.activeMode.mouseUp) {
      needSelection = vueApp.activeMode.mouseUp(event);
    }
    if (needSelection || vueApp.activeMode.toothSelected) {
      updateRaycasterFromEvent(event);
      let tooth = intersectTooth();
      vueApp.activeMode.toothSelected(tooth?.listItem, event);
    }
    return;
  }

  let now = new Date();
  let ts = now - mdTime;
  let upPoint = { x: event.clientX, y: event.clientY };
  let downUpDistance = getDistance2D(downPoint, upPoint);

  if (!isEditMode && !addToothGroup) return;

  updateRaycasterFromEvent(event);
  if (addToothGroup && event.button === 0 && ts < 200) {
    addingToothMouseUp();
  } else if (dragPoint) {
    endDragPoint();
  } else if (downUpDistance >= 0 && downUpDistance < 1) {
    if (selectedTooth && selectedTooth.listItem) {
      selectedTooth.listItem.isClicked = true;
    }

    if (event.button === 2 && selectedTooth) {
      changingToothMouseUp(event);
    } else if (event.button === 0 && ts < 400 && !viewMode.isFilledTeethView) {
      let tooth = intersectTooth();
      if (tooth) {
        updateSelectedTooth(tooth);
        if (viewMode.isEdit && viewMode.isBrushMode) updateBrush(event);
      }
    }
  }
  intersectedTooth = null;
  ioViewer.controls.enabled = true;
}

function onCameraChange() {
  // controls
  if (ioPlane) {
    ioPlane.lookAt(ioViewer.camera.position);
  }
}

function stopEvent(event, stopPropagation) {
  if (event) {
    if (event.preventDefault) {
      //  if (event.preventDefault != undefined) {
      event.preventDefault();
    }
    if (stopPropagation && event.stopPropagation) {
      event.stopPropagation();
    }
  }
}

//#endregion

//#region Adding tooth

function startAddingTooth(toothName, toothNumber) {
  console.log("startAddingTooth");
  addToothName = toothName;
  addToothNumber = toothNumber;
  document.getElementById("selectedToothHint").innerHTML = addToothName;
  $(".intraoral-edit-item").css("display", "none");

  if (!viewMode.isBrushMode) {
    $(".intraoral-add-item").css("display", "block");
    // isEditMode = false;

    let parentGroup = new THREE.Group();
    addToothGroup = new THREE.Group();
    parentGroup.add(addToothGroup);
    ioViewer.scene.add(parentGroup);

    if (selectedTooth) {
      if (selectedTooth.pointsGroup) selectedTooth.pointsGroup.visible = false;
      selectedTooth = null;
    }
  } else {
    let globalMesh = getGlobalMesh();
    let color = getColorFromToothNumber(addToothNumber);
    let model = {
      name: addToothName,
      baseNumber: 0,
      glbNumber: 0,
      number: addToothNumber,
      color: color, // "#00FF00",
      opacity: 1,
      isGingiva: false,
      isTooth: true,
      isUpper: globalMesh == upperMesh,
      isMaxilla: globalMesh == upperMesh,
      isAdded: true,
      isChanged: true,
      isBrushChanged: true,
      isVisible: true,
      recentlyAdded: true,
    };
    let material = pongMaterial.clone();
    material.color = new THREE.Color(color);
    let geometry = new THREE.BufferGeometry();
    geometry.setIndex([]);
    geometry.isSmallArea = true;
    viewMode.isBrushDraw = true;
    let mesh = new THREE.Mesh(geometry, material);
    model.mesh = model.mainMesh = mesh;
    // if (!model.name) model.name = getAddedToothName();
    // selectedTooth = model;
    updateSelectedTooth(model);
  }
}

function addingToothMouseUp() {
  if (addToothGroup) {
    let iogPoints = addToothGroup.children.filter(
      (item) => item.name === "point",
    );
    let length = iogPoints.length;
    let prevPoint = iogPoints[length - 1];

    let firstPointIntersected = length > 2 && intersectMesh(iogPoints[0]);
    if (firstPointIntersected) {
      let modelCameraDistance = addToothGroup.cameraDistance;
      let line = createLine(prevPoint, iogPoints[0], modelCameraDistance);
      addToothGroup.add(line);
      endAddingTooth(iogPoints);
    } else {
      addingToothAddPoint(prevPoint);
    }
  }
}

function addingToothAddPoint(prevPoint) {
  let groupPoint = intersectPoint(addToothGroup.parent);
  if (!groupPoint) {
    let scenePoint = intersectScene();
    if (scenePoint) {
      let modelCameraDistance = addToothGroup.cameraDistance;
      if (!modelCameraDistance) {
        modelCameraDistance = addToothGroup.cameraDistance = getDistance(
          ioViewer.camera.position,
          scenePoint,
        );
      }
      let globalGraph = getGlobalGraph();
      let index1 = getPointIndex(globalGraph, scenePoint, 0.01);
      let node = globalGraph.getNode(index1);
      let ioPoint = createIoPoint(node.point, modelCameraDistance);
      ioPoint.gindex = index1;
      if (prevPoint) {
        let line = createLine(prevPoint, ioPoint, modelCameraDistance);
        addToothGroup.add(line);
      }
      addToothGroup.add(ioPoint);
    }
  }
}

function endAddingTooth(iogPoints) {
  let allPoints = [];
  let iogLines = addToothGroup.children.filter((item) => item.name === "line");
  iogLines.forEach((line) => {
    let linePoints = line.points.slice(1);
    linePoints.forEach((point) => {
      allPoints.push(point);
    });
  });
  let globalMesh = getGlobalMesh();
  let globalGraph = getGlobalGraph();
  // iogPoints.forEach((p) => (p.visible = false));

  document.body.style.cursor = "wait";

  setTimeout(function () {
    let color = null;
    let model = null;
    if (selectedTooth) {
      color = selectedTooth.color;
      model = selectedTooth;
    } else {
      color = getColorFromToothNumber(addToothNumber);
      model = {
        name: addToothName,
        baseNumber: 0,
        glbNumber: 0,
        number: addToothNumber,
        color: color, // "#00FF00",
        opacity: 1,
        isGingiva: false,
        isTooth: true,
        isUpper: globalMesh == upperMesh,
        isMaxilla: globalMesh == upperMesh,
        isAdded: true,
        isChanged: true,
        isVisible: true,
        recentlyAdded: true,
      };
      if (!model.name) model.name = getAddedToothName();
      model.pointsGroup = addToothGroup.parent;
    }

    updateBezierPoints(iogPoints, globalGraph);

    let geometry = getFacesGeometry(
      model,
      allPoints,
      globalMesh,
      globalGraph,
      ioViewer.scene,
      [], //getCrossedPoints()
    );
    if (geometry) {
      geometry = prepareGeometry(geometry, false);
      geometry.computeBoundingBox();
      let material = pongMaterial.clone();
      material.color = new THREE.Color(color);

      let mesh = new THREE.Mesh(geometry, material);
      // mesh.updateMatrix();
      if (selectedTooth) {
        let mergedGeometry = getMergedGeometry([selectedTooth.mainMesh, mesh]);
        selectedTooth.mainMesh.geometry = selectedTooth.mainGeometry =
          mergedGeometry;
        selectedTooth.mesh = selectedTooth.mainMesh;
        console.log("endAddingTooth geometry", selectedTooth.mainMesh.geometry);
        // getGMergedGeometry(
        //   selectedTooth.mainMesh.geometry,
        //   geometry
        // );
        // BufferGeometryUtils.mergeGeometries([
        //   selectedTooth.mainMesh.geometry,
        //   geometry,
        // ]);
        addToothGroup.innerGeometry = geometry;
        setToothIsChanged(selectedTooth, true);
        selectedTooth.isBrushChanged = false;
        // selectedTooth.mainMesh.geometry.merge(mesh.geometry, mesh.matrix);
      } else {
        model.mesh = model.mainMesh = mesh;
        model.uuid = mesh.uuid;
        model.defaultBox = offsetBox(geometry.boundingBox);

        ioViewer.add_model(model);

        // ioViewer.scene.add(mesh);
        teethMeshes.push(mesh);
        teeth.push(model);
        allModels.push(model);
        selectedTooth = model;
        addItemInList(model);
      }
    } else {
      addToothGroup.parent.remove(addToothGroup);
      ioViewer.scene.remove(addToothGroup.parent);
    }
    // console.log(faces);
    $(".intraoral-add-item").css("display", "none");
    updateSelectedTooth(model);
    addToothGroup = addToothName = addToothNumber = null;
    // isEditMode = true;
    document.body.style.cursor = "default";
    $(".intraoral-edit-item").css("display", "block");
  }, 100);
}

function cancelAdd() {
  // isEditMode = true;
  console.log("cancelAdd", addToothGroup);
  $(".intraoral-add-item").css("display", "none");
  document.getElementById("selectedToothHint").innerHTML = "";
  if (isEditMode && !selectedTooth) {
    $(".intraoral-edit-item").css("display", "block");
  }
  if (addToothGroup) {
    addToothGroup.parent.remove(addToothGroup);
    addToothGroup = null;
  }
  addToothGroup = addToothName = addToothNumber = null;
}

//#endregion

//#region Changing tooth

function updatePointsGroupVisibility(model, visible) {
  const group = createPointsGroup(model);
  if (group) {
    group.visible = visible;
    if (visible) {
      let toothCameraDistance = getDistance(
        ioViewer.camera.position,
        model.defaultBox.center,
      );

      let pointMeshes = getPointMeshesFromGroup(group);
      pointMeshes.forEach((p) => {
        updateIoPointScale(p, toothCameraDistance);
        p.visible = isEditMode && !viewMode.isBrushMode;
      });

      let lineMeshes = getLineMeshesFromGroup(group);
      lineMeshes.forEach((line) =>
        updateTubeCurve(line, line.points, toothCameraDistance),
      );
    }
  }
}

function changingToothMouseUp(event) {
  if (selectedTooth && !isChangingToothMouseUp) {
    isChangingToothMouseUp = true;
    console.log("changingToothMouseUp");
    let point = intersectPoint(selectedTooth.pointsGroup);
    if (!point) {
      changingToothAddPoint(event);
    } else {
      let mainPoints = getMainPoints(selectedTooth, point.parent);
      if (mainPoints.length > 3) {
        setNextPrevPoints(point, mainPoints);
        changingToothRemovePoint(point);
      }
    }
    setToothIsChanged(selectedTooth, true);
    selectedTooth.isBrushChanged = false;
    setTimeout(function () {
      isChangingToothMouseUp = false;
    }, 400);
  }
  stopEvent(event);
}

async function changingToothAddPoint(event) {
  if (selectedTooth && (await checkIsEditMode(selectedTooth))) {
    updateRaycasterFromEvent(event);
    let intersectedLine = intersectSelectedBorder();
    let intersectedPoint1 = null;
    let intersectedPoint2 = null;
    if (intersectedLine) {
      let iparent = intersectedLine.parent;
      intersectedPoint1 = iparent.children.find(
        (c) => c.line1 == intersectedLine,
      );
      intersectedPoint2 = iparent.children.find(
        (c) => c.line2 == intersectedLine,
      );
      console.log(intersectedLine);
    }
    let point = intersectScene();
    if (point) {
      let areaPoints = [];
      let globalGraph = getGlobalGraph();

      let oldGeometry = selectedTooth.mesh.geometry.clone();
      let oldAllPoints = getAllBorderPointsArr(selectedTooth);
      let oldAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
      let oldAnchorGroup = getSavedAnchorGroup(selectedTooth);

      let newIndex = 0;
      let group = null;
      let mainPoints = null;
      if (intersectedPoint1 && intersectedPoint2) {
        group = intersectedPoint1.parent;

        mainPoints = getMainPoints(selectedTooth, group);
        newIndex = mainPoints.indexOf(intersectedPoint2);
      } else {
        let npoint = getNearestMainPoint(point);
        group = npoint.parent; //selectedTooth.pointsGroup;

        // npoint.material.color = new THREE.Color("#FF0000");
        mainPoints = getMainPoints(selectedTooth, group);
        newIndex = mainPoints.indexOf(npoint);

        setNextPrevPoints(npoint, mainPoints);
        let prevDist = getSquaredDistance(point, npoint.prevPoint.position);
        let nextDist = getSquaredDistance(point, npoint.nextPoint.position);
        if (prevDist > nextDist) {
          newIndex++;
        }
      }

      let modelCameraDistance = getModelCameraDistance(selectedTooth);
      let newPoint = createIoPoint(point, modelCameraDistance);
      mainPoints.splice(newIndex, 0, newPoint);
      setNextPrevPoints(newPoint, mainPoints);

      let index0 = newPoint.prevPoint.gindex;
      let index2 = newPoint.nextPoint.gindex;
      let index1 = getPointIndex(globalGraph, newPoint.position, 0.01);
      if (index1 == index2 || index1 == index0) {
        return;
      }

      let lineIndex = group.children.indexOf(newPoint.prevPoint.line1);
      group.remove(newPoint.prevPoint.line1);
      areaPoints = areaPoints.concat(newPoint.prevPoint.line1.points);

      let points1 = getShortPath(globalGraph, index1, index2);
      areaPoints = areaPoints.concat(points1.toReversed().slice(1));
      let line1 = createTubeCurve(points1, modelCameraDistance);
      group.children.splice(lineIndex, 0, line1);
      line1.parent = group;

      line1.point1 = points1[0];
      line1.point2 = points1[points1.length - 1];
      newPoint.line1 = line1;
      newPoint.position.copy(line1.point1);
      group.children.splice(lineIndex, 0, newPoint);
      newPoint.parent = group;
      newPoint.nextPoint.line2 = line1;

      let points2 = getShortPath(globalGraph, index0, index1);
      areaPoints = areaPoints.concat(points2.slice(1).toReversed().slice(1));
      let line2 = createTubeCurve(points2, modelCameraDistance);
      group.children.splice(lineIndex, 0, line2);
      line2.parent = group;
      line2.point1 = points2[0];
      line2.point2 = points2[points2.length - 1];
      newPoint.line2 = line2;
      newPoint.prevPoint.line1 = line2;

      document.body.style.cursor = "wait";

      updateBezierPoints(mainPoints, globalGraph);

      // let tooth = intersectTooth();
      // let isClearing = tooth == selectedTooth;

      let rootPoint = newPoint.position.clone();

      let intersectedPoint = intersectFirst(
        selectedTooth.mesh,
        ioRaycaster,
        rootPoint,
      );
      let isClearing = !!intersectedPoint;

      // if (!isClearing)
      //   fillArea(
      //     selectedTooth,
      //     areaPoints,
      //     rootPoint,
      //     globalGraph,
      //     ioViewer.scene,
      //     isClearing
      //   );

      let newGeometry = selectedTooth.mesh.geometry.clone();
      let newAllPoints = getAllBorderPointsArr(selectedTooth);
      let newAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
      let newAnchorGroup = getSavedAnchorGroup(selectedTooth);
      let change = {
        model: selectedTooth,
        oldGeometry: oldGeometry,
        oldPoints: oldAllPoints,
        oldAnchorPoints: oldAnchorPoints,
        oldAnchorGroup: oldAnchorGroup,
        newGeometry: newGeometry,
        newPoints: newAllPoints,
        newAnchorPoints: newAnchorPoints,
        newAnchorGroup: newAnchorGroup,
      };
      console.log("changingToothAddPoint ioChange", change);
      addIoChange(change);

      document.body.style.cursor = "default";
      return;
      setTimeout(function () {
        let allPoints = getAllBorderPoints(selectedTooth);
        let globalMesh = getGlobalMesh();
        let geometry = getFacesGeometry(
          selectedTooth,
          allPoints,
          globalMesh,
          globalGraph,
          ioViewer.scene,
          getCrossedPoints(),
        );
        if (geometry) {
          selectedTooth.mesh.geometry.dispose();
          selectedTooth.mesh.geometry = selectedTooth.mainGeometry = geometry;
        }
        document.body.style.cursor = "default";
      }, 100);
    }
  }
}

async function changingToothRemovePoint(point) {
  if (selectedTooth && (await checkIsEditMode(selectedTooth))) {
    clearTempMeshes(ioViewer.scene);
    let areaPoints = [];

    let mainPoints = getMainPoints(selectedTooth, point.parent);

    let oldGeometry = selectedTooth.mesh.geometry.clone();
    let oldAllPoints = getAllBorderPointsArr(selectedTooth);
    let oldAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
    let oldAnchorGroup = getSavedAnchorGroup(selectedTooth);

    let pindex = mainPoints.indexOf(point);
    mainPoints.splice(pindex, 1);
    let group = point.parent;
    let lineIndex = group.children.indexOf(point.line2);
    group.remove(point);
    group.remove(point.line1);
    group.remove(point.line2);
    // let tubeRadius = point.line1.tubeRadius;

    areaPoints = areaPoints.concat(point.line2.points);
    areaPoints = areaPoints.concat(point.line1.points.slice(1));

    let p1 = point.prevPoint;
    let p2 = point.nextPoint;
    let globalGraph = getGlobalGraph();
    let points = getShortPath(globalGraph, p1.gindex, p2.gindex);

    areaPoints = areaPoints.concat(points.slice(1).toReversed().slice(1));

    let modelCameraDistance = getModelCameraDistance(selectedTooth);
    let newLine = createTubeCurve(points, modelCameraDistance);
    newLine.points = newLine.shortPoints = points;
    newLine.point1 = p1.position;
    newLine.point2 = p2.position;
    p1.line1 = newLine;
    p2.line2 = newLine;
    group.children.splice(lineIndex, 0, newLine);
    newLine.parent = group;

    document.body.style.cursor = "wait";

    updateBezierPoints(mainPoints, globalGraph);

    let isClearing = true;
    let middlePoint = points[Math.floor(points.length / 2)];
    if (middlePoint) {
      let origin = ioViewer.camera.position.clone();
      // let origin = new THREE.Vector3(
      //   middlePoint.x,
      //   middlePoint.y + 10,
      //   middlePoint.z
      // );
      let target = middlePoint.clone();
      let direction = target.sub(origin).normalize();
      updateIoRaycaster(origin, direction);

      let intersectedPoint = intersectFirst(
        selectedTooth.mesh,
        ioRaycaster,
        middlePoint,
      );
      isClearing = !!intersectedPoint;
      console.log("changingToothRemovePoint ", isClearing);
      // if (intersectedPoint) {
      //   drawSpherePoint(ioViewer.scene, intersectedPoint, 0x0000ff);
      // } else {
      //   drawSpherePoint(ioViewer.scene, middlePoint);
      // }
    }

    // if (!isClearing)
    //   fillArea(
    //     selectedTooth,
    //     areaPoints,
    //     middlePoint,
    //     globalGraph,
    //     ioViewer.scene,
    //     isClearing
    //   );

    let newGeometry = selectedTooth.mesh.geometry.clone();
    let newAllPoints = getAllBorderPointsArr(selectedTooth);
    let newAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
    let newAnchorGroup = getSavedAnchorGroup(selectedTooth);
    let change = {
      model: selectedTooth,
      oldGeometry: oldGeometry,
      oldPoints: oldAllPoints,
      oldAnchorPoints: oldAnchorPoints,
      oldAnchorGroup: oldAnchorGroup,
      newGeometry: newGeometry,
      newPoints: newAllPoints,
      newAnchorPoints: newAnchorPoints,
      newAnchorGroup: newAnchorGroup,
    };
    // console.log("changingToothRemovePoint ioChange", change);
    addIoChange(change);

    document.body.style.cursor = "default";
    return;

    setTimeout(function () {
      let allPoints = getAllBorderPoints(selectedTooth);
      let globalMesh = getGlobalMesh();
      let geometry = getFacesGeometry(
        selectedTooth,
        allPoints,
        globalMesh,
        globalGraph,
        ioViewer.scene,
        getCrossedPoints(),
      );
      if (geometry) {
        selectedTooth.mesh.geometry.dispose();
        selectedTooth.mesh.geometry = selectedTooth.mainGeometry = geometry;
      }
      document.body.style.cursor = "default";
    }, 100);
  }
}

function updateSelectedTooth(tooth, skipBrushValidation) {
  // console.log("updateSelectedTooth", tooth);
  document.querySelector("#intraoralBrushButtons").style.display = "none";
  teeth.forEach((model) => {
    if (!model.isDeleted && model.isVisible && model.isUpper == isUpperMode) {
      let mesh = ioViewer.get_model_mesh(model.id);
      mesh.visible = true;
    }

    if (model.pointsGroup) {
      model.pointsGroup.visible = false;
      // model.mesh.material.polygonOffsetFactor = 3;
    }
    if (model.borderMesh) {
      model.borderMesh.visible = false;
    }
  });
  let globalMesh = getGlobalMesh();
  if (globalMesh) {
    if (globalMesh.highlightMesh) {
      globalMesh.highlightMesh.visible = false;
    }
    if (globalMesh.borderMesh) {
      globalMesh.borderMesh.visible = false;
    }
  }

  if (tooth) {
    // if (selectedTooth != tooth) {
    selectedTooth = tooth;
    // setToothIsChanged(selectedTooth, true);
    // tooth.mesh.material.polygonOffsetFactor = 2;
    // document.getElementById("selectedToothHint").innerHTML = `" ${tooth.name} "`;
    document.getElementById("selectedToothHint").innerHTML = tooth.name;
    $(".intraoral-edit-item").css("display", "none");

    if (viewMode.isBrushMode && isEditMode) {
      if (viewMode.isSaving) {
        swal({
          text: "Please wait for the end of the save operation.",
        });
        updateSelectedTooth(null);
        return;
      }
      let selectedMesh = selectedTooth.mesh; //ioViewer.get_model_mesh(selectedTooth.id);
      let highlightResult = updateHighlightMeshFromToothMesh(selectedMesh);
      if (!highlightResult && !skipBrushValidation) {
        swal({
          text: "Selected tooth is invalid. Do you want to fix case on server?",
          buttons: {
            yes: { text: "Yes", value: true },
            no: { text: "No", value: false },
          },
        }).then((save) => {
          if (save) {
            updateSelectedTooth(null);
            saveIntraoralData(false, true, false, false);
          } else {
            document.querySelector("#intraoralBrushButtons").style.display =
              "flex";
            globalMesh.highlightMesh.material = selectedMesh.material.clone();

            selectedMesh.visible = false;
            selectedMesh.geometry.isSmallArea = true;
            viewMode.isBrushDraw = true;
            globalMesh.highlightMesh.visible = true;

            updateGlobalBorderMesh();
          }
        });
      } else {
        document.querySelector("#intraoralBrushButtons").style.display = "flex";
        globalMesh.highlightMesh.material = selectedMesh.material.clone();

        selectedMesh.visible = false;
        globalMesh.highlightMesh.visible = true;

        updateGlobalBorderMesh();
      }
    } else {
      updatePointsGroupVisibility(tooth, !selectedTooth.listItem.isFocused);
    }
  } else {
    if (brushMesh) brushMesh.visible = false;
    selectedTooth = null;
    document.getElementById("selectedToothHint").innerHTML = "";
    if (isEditMode) {
      $(".intraoral-edit-item").css("display", "block");
    }
  }
}

function setToothIsChanged(tooth, isChanged) {
  if (tooth) {
    tooth.isChanged = isChanged;
    if (tooth.listItem) {
      tooth.listItem.isChanged = isChanged;
    }
  }
}

//#endregion

//#region Brush Mode

function updateHighlightMeshFromToothMesh(mesh) {
  let isValid = false;

  try {
    const globalMesh = getGlobalMesh();
    const meshGeometry = mesh.geometry;
    console.time("verticesMap");
    if (!mesh.verticesMap) {
      mesh.verticesMap = getVerticesMap(meshGeometry, mesh.name);
    }

    const toothGeometry = mesh.geometry;
    // const toothPositionAttr = toothGeometry.getAttribute("position");
    const globalGeometry = globalMesh.geometry;
    // const globalPositionAttr = globalGeometry.getAttribute("position");

    if (!mesh.indexMap) {
      const globalGeometry = globalMesh.geometry;
      if (!globalMesh.verticesMap) {
        globalMesh.verticesMap = getVerticesMap(globalGeometry, "global");
      }

      if (mesh.verticesMap && globalMesh.verticesMap) {
        mesh.indexMap = {};
        for (const hash in mesh.verticesMap) {
          if (hash in globalMesh.verticesMap) {
            let globalIndex = globalMesh.verticesMap[hash].index;
            // let globalVector = new THREE.Vector3().fromBufferAttribute(
            //   globalPositionAttr,
            //   globalIndex
            // );
            let toothIndex = mesh.verticesMap[hash].index;
            // let toothVector = new THREE.Vector3().fromBufferAttribute(
            //   toothPositionAttr,
            //   toothIndex
            // );
            // if (globalIndex == 0) {
            //   console.log("globalIndex 0", globalIndex);
            // }

            // mesh.verticesMap[hash].globalIndex = globalIndex;
            mesh.indexMap[toothIndex] = globalIndex;
          }
          // else {
          //   console.log("missed hash", hash);
          // }
        }
      }
    }
    console.timeEnd("verticesMap");

    let globalIndices = [];
    const indexAttr = mesh.geometry.getIndex();
    let indexCount = indexAttr.count;
    for (let i = 0; i < indexCount; i += 3) {
      let index1 = indexAttr.getX(i);
      let index2 = indexAttr.getY(i);
      let index3 = indexAttr.getZ(i);
      let globalIndex1 = mesh.indexMap[index1];
      let globalIndex2 = mesh.indexMap[index2];
      let globalIndex3 = mesh.indexMap[index3];
      if (
        globalIndex1 === undefined ||
        globalIndex2 === undefined ||
        globalIndex3 === undefined
      ) {
        isValid = false;
        // console.log("undefined globalIndex");
      } else {
        globalIndices.push(globalIndex1, globalIndex2, globalIndex3);
      }
    }
    isValid = globalIndices.length === indexCount;
    // isValid = true;
    // let globalIndices = indexAttr.array.map((i) => mesh.indexMap[i]);
    // console.log("globalIndices", globalIndices);
    globalMesh.highlightMesh.geometry.drawRange.count = 0;
    brushedFaces.clear();
    if (isValid) {
      updateHighLightMeshFromIndices(globalIndices, true, true);
    }

    // mesh.geometry.computeBoundingSphere();
    // let sphere = mesh.geometry.boundingSphere;
    // const globalIndices = getGeometryIndexesFromSphere(geometry, sphere);
    // console.log("verticesMap 2", mesh.verticesMap);
  } catch (error) {
    console.error("updateHighlightMeshFromToothMesh error", error);
    isValid = false;
  }
  return isValid;
}

function getVerticesMap(geometry, tag, needFix) {
  console.time("getVerticesMap " + tag);
  let verticesMap = null;
  let isValid = false;
  const positionAttr = geometry ? geometry.getAttribute("position") : null;
  let indexCount = positionAttr ? positionAttr.count : 0;
  if (indexCount > 0) {
    verticesMap = {}; //new Map();

    const precisionPoints = 4;
    const precision = Math.pow(10, precisionPoints);

    let vector = new THREE.Vector3();
    let hash = null;

    for (let i = 0; i < indexCount; i++) {
      vector.fromBufferAttribute(positionAttr, i);
      hash = `${Math.round(vector.x * precision)},${Math.round(
        vector.y * precision,
      )},${Math.round(vector.z * precision)}`;
      if (!(hash in verticesMap)) {
        verticesMap[hash] = {
          index: i,
          // x: vector.x,
          // y: vector.y,
          // z: vector.z,
        };
      } else {
        isValid = false;
      }

      // if (!verticesMap.has(hash))
      // verticesMap.set(hash, {
      //   index: index,
      //   x: vector.x,
      //   y: vector.y,
      //   z: vector.z,
      // });
    }
  }
  console.timeEnd("getVerticesMap " + tag);
  return verticesMap;
}

function updateGlobalBorderMesh() {
  let globalMesh = getGlobalMesh();
  let highlightMesh = globalMesh.highlightMesh;

  if (globalMesh.borderMesh) {
    globalMesh.borderMesh.children.forEach((child) => {
      child.geometry.dispose();
      child.material.dispose();
    });

    // ioViewer.renderer.renderLists.dispose();
    ioViewer.scene.remove(globalMesh.borderMesh);
  }

  globalMesh.borderMesh = createBordersForMesh(highlightMesh);
  if (globalMesh.borderMesh) ioViewer.scene.add(globalMesh.borderMesh);
}

function createBordersForMesh(mesh) {
  let bordersGeometries = calculateBordersGeometries(mesh.geometry);
  if (bordersGeometries && bordersGeometries.length) {
    let modelCameraDistance = getModelCameraDistance(selectedTooth);
    const group = new THREE.Group();
    for (const bordersGeometry of bordersGeometries) {
      let bordersMesh = createTubeCurve(
        bordersGeometry.points,
        modelCameraDistance,
      );
      bordersMesh.points = bordersGeometry.points;
      // new THREE.Line(bordersGeometry);
      // let globalMesh = getGlobalMesh();
      // bordersMesh.material = globalMesh.material.clone();
      // bordersMesh.material.polygonOffsetFactor = -90;
      // bordersMesh.material.color.set(0x000000);

      group.add(bordersMesh);
      group.renderOrder = 2000;
    }
    return group;
  }
}

function calculateBordersGeometries(geometry) {
  let bordersGeometries = [];
  if (geometry !== null) {
    const _v0 = new THREE.Vector3();
    const _v1 = new THREE.Vector3();
    const _triangle = new THREE.Triangle();

    const precisionPoints = 4;
    const precision = Math.pow(10, precisionPoints);

    const indexAttr = geometry.getIndex();
    const positionAttr = geometry.getAttribute("position");
    let indexCount = indexAttr ? indexAttr.count : positionAttr.count;
    if (isFinite(geometry.drawRange.count))
      indexCount = geometry.drawRange.count;

    const indexArr = [0, 0, 0];
    const hashes = new Array(3);
    const edgeData = {};
    for (let i = 0; i < indexCount; i += 3) {
      if (indexAttr) {
        indexArr[0] = indexAttr.getX(i);
        indexArr[1] = indexAttr.getX(i + 1);
        indexArr[2] = indexAttr.getX(i + 2);
      } else {
        indexArr[0] = i;
        indexArr[1] = i + 1;
        indexArr[2] = i + 2;
      }

      const { a, b, c } = _triangle;
      a.fromBufferAttribute(positionAttr, indexArr[0]);
      b.fromBufferAttribute(positionAttr, indexArr[1]);
      c.fromBufferAttribute(positionAttr, indexArr[2]);

      // create hashes for the edge from the vertices
      hashes[0] = `${Math.round(a.x * precision)},${Math.round(
        a.y * precision,
      )},${Math.round(a.z * precision)}`;
      hashes[1] = `${Math.round(b.x * precision)},${Math.round(
        b.y * precision,
      )},${Math.round(b.z * precision)}`;
      hashes[2] = `${Math.round(c.x * precision)},${Math.round(
        c.y * precision,
      )},${Math.round(c.z * precision)}`;

      // skip degenerate triangles
      if (
        hashes[0] === hashes[1] ||
        hashes[1] === hashes[2] ||
        hashes[2] === hashes[0]
      ) {
        continue;
      }

      // iterate over every edge
      for (let j = 0; j < 3; j++) {
        const jNext = (j + 1) % 3;
        const vecHash0 = hashes[j];
        const vecHash1 = hashes[jNext];

        const hash = `${vecHash0}_${vecHash1}`;
        const reverseHash = `${vecHash1}_${vecHash0}`;

        if (reverseHash in edgeData) {
          edgeData[reverseHash] = null;
        } else if (!(hash in edgeData)) {
          // if we've already got an edge here then skip adding a new one
          edgeData[hash] = {
            index0: indexArr[j],
            index1: indexArr[jNext],
          };
        }
      }
    }

    let segments = [];
    // iterate over all remaining, unmatched edges and add them to the vertex array
    let vertexHashes = {};
    for (const key in edgeData) {
      if (edgeData[key]) {
        let { index0, index1 } = edgeData[key];
        _v0.fromBufferAttribute(positionAttr, index0);
        let hash0 = `${Math.round(_v0.x * precision)},${Math.round(
          _v0.y * precision,
        )},${Math.round(_v0.z * precision)}`;
        if (hash0 in vertexHashes) index0 = vertexHashes[hash0];
        else {
          vertexHashes[hash0] = index0;
        }

        _v1.fromBufferAttribute(positionAttr, index1);
        let hash1 = `${Math.round(_v1.x * precision)},${Math.round(
          _v1.y * precision,
        )},${Math.round(_v1.z * precision)}`;
        if (hash1 in vertexHashes) index1 = vertexHashes[hash1];
        else {
          vertexHashes[hash1] = index1;
        }

        let segment = {
          p1: index0,
          p2: index1,
          // v1: _v0.clone(),
          // v2: _v1.clone(),
        };
        segments.push(segment);
        // vertices.push(_v0.x, _v0.y, _v0.z);
        // vertices.push(_v1.x, _v1.y, _v1.z);
      }
    }

    while (segments.length > 0) {
      let segmVertices = getVerticesFromSegments(segments);
      if (segmVertices && segmVertices.length) {
        const vertices = [];
        const points = [];
        for (const index0 of segmVertices) {
          _v0.fromBufferAttribute(positionAttr, index0);
          vertices.push(_v0.x, _v0.y, _v0.z);
          points.push(_v0.clone());
        }
        if (points.length > 4) {
          let bordersGeometry = new THREE.BufferGeometry();
          bordersGeometry.setAttribute(
            "position",
            new THREE.Float32BufferAttribute(vertices, 3),
          );
          bordersGeometry.points = points;
          bordersGeometries.push(bordersGeometry);
        }
      }
    }
  }

  return bordersGeometries;
}

function getVerticesFromSegments(segments) {
  let vertices = [];
  let head = segments.length ? segments[0] : null;
  let tail = head;
  let headIndex = 0;
  let tailIndex = headIndex;
  vertices.push(tail.p1);
  while (tail) {
    vertices.push(tail.p2);
    segments.splice(tailIndex, 1);
    tailIndex = segments.findIndex((segment) => segment.p1 == tail.p2);
    tail = tailIndex >= 0 ? segments[tailIndex] : null;
  }
  return vertices;
}

async function switchToContourMode() {
  ioViewer.controls.enabled = true;
  brushMesh.visible = false;
  if (!viewMode.isBrushMode) return;
  viewMode.isBrushMode = false;

  let toothNumber = addToothNumber;
  let toothName = addToothName;

  if (addToothName) {
    cancelBrush();
  } else if (
    selectedTooth &&
    selectedTooth.isVisible &&
    !selectedTooth.isDeleted
  ) {
    await completeBrush(true);
  }
  updatePointsGroupVisibility(selectedTooth, isEditMode);

  let globalMesh = getGlobalMesh();
  globalMesh.highlightMesh.geometry.drawRange.count = 0;
  globalMesh.highlightMesh.visible = false;
  if (globalMesh.borderMesh) ioViewer.scene.remove(globalMesh.borderMesh);

  brushedFaces.clear();

  document.querySelector("#intraoralBrushButtons").style.display = "none";

  if (toothName) {
    startAddingTooth(toothName, toothNumber);
  }
}

function switchToBrushMode(skipBrushValidation) {
  viewMode.isBrushMode = true;
  let toothNumber = addToothNumber;
  let toothName = addToothName;
  cancelAdd();

  updateSelectedTooth(selectedTooth, skipBrushValidation);
  if (selectedTooth) {
    document.querySelector("#intraoralBrushButtons").style.display = "flex";
  }

  if (toothName) {
    startAddingTooth(toothName, toothNumber);
  }
}

function updateBrush(event) {
  if (!brushMesh || !selectedTooth) {
    return;
  }

  updateRaycasterFromEvent(event);

  let globalMesh = getGlobalMesh();
  let point = intersectMesh(globalMesh);

  if (!point) {
    brushMesh.visible = false;
    ioViewer.controls.enabled = true;
    return;
  }

  brushMesh.position.copy(point);
  brushMesh.visible = true;

  const inverseMatrix = new THREE.Matrix4();
  inverseMatrix.copy(globalMesh.matrixWorld).invert();

  const sphere = new THREE.Sphere();
  sphere.center.copy(brushMesh.position).applyMatrix4(inverseMatrix);
  sphere.radius = viewMode.brushSize / 5;

  if (
    event.buttons == 1 &&
    event.ctrlKey &&
    (isBrushStartedOnTooth || !viewMode.isBrushDraw || isSmallAreaSelected)
  ) {
    ioViewer.controls.enabled = false;
    const geometry = globalMesh.geometry;
    const indices = getGeometryIndexesFromSphere(geometry, sphere);
    updateHighLightMeshFromIndices(indices, viewMode.isBrushDraw, false);
    mouseCameraPoint = brushMesh.position;
  }
}

function updateHighLightMeshFromIndices(indices, isBrushDraw, fromMesh) {
  if (indices && indices.length) {
    let globalMesh = getGlobalMesh();
    const indexAttr = globalMesh.geometry.index;
    const highlightMesh = globalMesh.highlightMesh;
    const oldDrawCount = highlightMesh.geometry.drawRange.count;
    const highlightIndexAttr = highlightMesh.geometry.index;
    let newDrawCount = highlightMesh.geometry.drawRange.count;

    if (isBrushDraw) {
      for (let i = 0, l = indices.length; i < l; i += 3) {
        const a = fromMesh ? indices[i] : indexAttr.getX(indices[i]);
        const b = fromMesh ? indices[i + 1] : indexAttr.getY(indices[i]);
        const c = fromMesh ? indices[i + 2] : indexAttr.getZ(indices[i]);
        const faseString = `${a},${b},${c}`;
        if (!brushedFaces.has(faseString)) {
          brushedFaces.set(faseString, [a, b, c]);
          highlightIndexAttr.setX(newDrawCount++, a);
          highlightIndexAttr.setX(newDrawCount++, b);
          highlightIndexAttr.setX(newDrawCount++, c);
        }
      }
    } else if (brushedFaces.size > 0) {
      let needUpdate = false;
      for (let i = 0, l = indices.length; i < l; i += 3) {
        const a = fromMesh ? indices[i] : indexAttr.getX(indices[i]);
        const b = fromMesh ? indices[i + 1] : indexAttr.getY(indices[i]);
        const c = fromMesh ? indices[i + 2] : indexAttr.getZ(indices[i]);
        const faseString = `${a},${b},${c}`;
        if (brushedFaces.has(faseString)) {
          brushedFaces.delete(faseString);
          needUpdate = true;
        }
      }
      if (needUpdate) {
        const newIndices = [...brushedFaces.values()].flat();
        newDrawCount = newIndices.length;
        for (let i = 0; i < newDrawCount; i++) {
          highlightIndexAttr.setX(i, newIndices[i]);
        }
        // highlightMesh.geometry.setIndex(newIndices);
        // highlightIndexAttr = highlightMesh.geometry.index;
      }
    }

    if (oldDrawCount != newDrawCount) {
      highlightMesh.geometry.drawRange.count = newDrawCount;
      // newIndexAttr.clearUpdateRanges();
      // newIndexAttr.addUpdateRange(0, highlightMesh.geometry.drawRange.count);
      highlightIndexAttr.needsUpdate = true;
    }
  }
}

function getGeometryIndexesFromSphere(geometry, sphere) {
  const indices = [];
  const bvh = geometry.boundsTree;
  const tempVec = new THREE.Vector3();
  bvh.shapecast({
    intersectsBounds: (box) => {
      const intersects = sphere.intersectsBox(box);
      const { min, max } = box;
      if (intersects) {
        for (let x = 0; x <= 1; x++) {
          for (let y = 0; y <= 1; y++) {
            for (let z = 0; z <= 1; z++) {
              tempVec.set(
                x === 0 ? min.x : max.x,
                y === 0 ? min.y : max.y,
                z === 0 ? min.z : max.z,
              );
              if (!sphere.containsPoint(tempVec)) {
                return INTERSECTED;
              }
            }
          }
        }

        return CONTAINED;
      }

      return intersects ? INTERSECTED : NOT_INTERSECTED;
    },

    intersectsTriangle: (tri, i, contained) => {
      if (contained || tri.intersectsSphere(sphere)) {
        const i3 = 3 * i;
        indices.push(i3, i3 + 1, i3 + 2);
      }

      return false;
    },
  });
  return indices;
}

function switchBrushDrawMode(isBrushDraw) {
  viewMode.isBrushDraw = isBrushDraw;
}

function updateBrushMesh() {
  if (brushMesh) {
    const brushGeometry = new THREE.SphereGeometry(
      viewMode.brushSize / 5,
      32,
      32,
    );
    const oldGeometry = brushMesh.geometry;
    brushMesh.geometry = brushGeometry;
    oldGeometry.dispose();
  }
}

async function completeBrush(reserveSelection, askForMultiBorders) {
  if (selectedTooth) {
    setToothIsChanged(selectedTooth, true);
    selectedTooth.isBrushChanged = true;
    const globalMesh = getGlobalMesh();
    const borderMesh = globalMesh.borderMesh;

    let mesh = selectedTooth.mesh; //ioViewer.get_model_mesh(selectedTooth.id);
    let highlightedGeometry = getHighlightedGeometry();
    if (highlightedGeometry) {
      mesh.verticesMap = mesh.indexMap = null;
      mesh.geometry = selectedTooth.mainGeometry = highlightedGeometry;
    }
    mesh.visible = true;
    selectedTooth.defaultBox = offsetBox(mesh.geometry.boundingBox);

    if (!teeth.includes(selectedTooth)) {
      let model = selectedTooth;
      model.uuid = mesh.uuid;

      ioViewer.add_model(model);

      // ioViewer.scene.add(mesh);
      teethMeshes.push(mesh);
      teeth.push(model);
      allModels.push(model);
      selectedTooth = model;
      addItemInList(model);
    }

    if (borderMesh && borderMesh.children && borderMesh.children.length) {
      // selectedTooth.pointsGroup = null;
      // selectedTooth.points = borderMesh.children[0].points;
      let bordersGroup = [];
      if (borderMesh.children.length > 1 && askForMultiBorders) {
        let option = await swal({
          text: `There are several fragments for tooth ${selectedTooth.number}. Do you want to save the largest one or all fragments?`,
          buttons: {
            largest: { text: "Largest", value: "largest" },
            all: { text: "All Segments", value: "all" },
          },
        });
        console.log("completeBrush option", option);

        if (option === "largest") {
          let maxBorderMesh = borderMesh.children[0];
          let maxPoints = maxBorderMesh.points;
          borderMesh.children.forEach((border) => {
            if (border.points.length > maxPoints.length)
              maxPoints = border.points;
          });
          bordersGroup.push(maxPoints);

          let tempGeometry = highlightedGeometry.clone();

          let islands = split_mesh_islands(highlightedGeometry);
          console.log("islands", islands);
          if (islands && islands.length) {
            tempGeometry = islands[0];
            let maxCount = tempGeometry.attributes.position.count;
            for (let i = 0; i < islands.length; i++) {
              const island = islands[i];
              if (island.attributes.position.count > maxCount) {
                tempGeometry = island;
                maxCount = tempGeometry.attributes.position.count;
              }
            }
          }
          // borderMesh.children.forEach((border) => {
          //   if (border != maxBorderMesh) {
          //     let fullBrush = new Brush(tempGeometry);

          //     border.geometry.computeBoundingBox();
          //     let toothBox = offsetBox(border.geometry.boundingBox);
          //     let boxGeometry = new BoxGeometry(
          //       toothBox.size.x,
          //       toothBox.size.y,
          //       toothBox.size.z
          //     );
          //     boxGeometry.translate(
          //       toothBox.center.x,
          //       toothBox.center.y,
          //       toothBox.center.z
          //     );
          //     let boxBrush = new Brush(border.geometry); // new Brush(boxGeometry);
          //     boxBrush.updateMatrixWorld();

          //     let tempMesh = csgEvaluator.evaluate(
          //       fullBrush,
          //       boxBrush,
          //       HOLLOW_SUBTRACTION
          //     );
          //     tempGeometry = tempMesh.geometry;
          //   }
          // });
          mesh.geometry = selectedTooth.mainGeometry =
            prepareGeometry(tempGeometry);
        }
      }
      if (bordersGroup.length === 0) {
        borderMesh.children.forEach((border) =>
          bordersGroup.push(border.points),
        );
      }

      createPointsGroupFromBorders(selectedTooth, bordersGroup);
      if (drawChange) {
        drawChange.newGeometry = mesh.geometry.clone();
        drawChange.newPoints = getAllBorderPointsArr(selectedTooth);
        drawChange.newAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
        drawChange.newAnchorGroup = getSavedAnchorGroup(selectedTooth);
        addIoChange(drawChange);
        drawChange = null;
      }
    }

    // createPointsGroupBase(selectedTooth);
  }

  let tempTooth = selectedTooth;
  cancelBrush();
  if (reserveSelection) updateSelectedTooth(tempTooth);
}

function getHighlightedGeometry() {
  let newGeometry = null;
  try {
    const globalMesh = getGlobalMesh();
    const highlightMesh = globalMesh.highlightMesh;
    console.log("convertHighlightMesh", highlightMesh);
    const geometry = highlightMesh.geometry;

    const indexAttr = geometry.getIndex();
    const positionAttr = geometry.getAttribute("position");
    let indexCount = indexAttr ? indexAttr.count : positionAttr.count;
    if (isFinite(geometry.drawRange.count))
      indexCount = geometry.drawRange.count;

    const verticesArr = [];
    const vector = new THREE.Vector3();
    for (let i = 0; i < indexCount; i++) {
      const index = indexAttr ? indexAttr.getX(i) : i;
      vector.fromBufferAttribute(positionAttr, index);
      verticesArr.push(vector.x, vector.y, vector.z);
    }

    newGeometry = new THREE.BufferGeometry();
    newGeometry.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(verticesArr), 3),
    );
    newGeometry = prepareGeometry(newGeometry, false);
    console.log("newGeometry", newGeometry);
  } catch (error) {
    console.log("getHighLightedGeometry ERROR", error);
  }

  return newGeometry;
}

function cancelBrush() {
  let globalMesh = getGlobalMesh();
  globalMesh.highlightMesh.geometry.drawRange.count = 0;
  if (globalMesh.borderMesh) ioViewer.scene.remove(globalMesh.borderMesh);
  brushedFaces.clear();

  updateSelectedTooth(null);
  document.querySelector("#intraoralBrushButtons").style.display = "none";
}

//#endregion

//#region Drag point

async function startDragPoint(point, event) {
  ioViewer.controls.enabled = false;

  if (event.button === 0 && (await checkIsEditMode(selectedTooth))) {
    let mainPoints = getMainPoints(selectedTooth, point.parent);
    setNextPrevPoints(point, mainPoints);
    dragPoint = point;
    dragPoint.basePosition = point.position.clone();
    dragPoint.baseIndex = getPointIndex(
      getGlobalGraph(),
      dragPoint.basePosition,
    );
    if (event.preventDefault) event.preventDefault();
  }
}

function moveDragPoint(event) {
  if (dragPoint) {
    updateRaycasterFromEvent(event);
    let point = intersectScene();
    if (point) {
      dragPoint.position.copy(point);

      if (dragPoint.line1) {
        let line = dragPoint.line1;
        updateTubeLine(line, point, line.point2);
        line.point1 = point;
      }

      if (dragPoint.line2) {
        let line = dragPoint.line2;
        updateTubeLine(line, line.point1, point);
        line.point2 = point;
      }
    }
  }
}

function endDragPoint() {
  clearTempMeshes(ioViewer.scene);

  if (dragPoint) {
    let globalGraph = getGlobalGraph();
    let index1 = getPointIndex(globalGraph, dragPoint.position, 0.01);
    let newPosition = globalGraph.getNode(index1).position;
    if (
      index1 === dragPoint.baseIndex ||
      areNearPoints(dragPoint.basePosition, newPosition)
    ) {
      let line = dragPoint.line1;
      updateTubeCurve(
        line,
        line.bezierPoints ? line.bezierPoints : line.points,
      );
      line = dragPoint.line2;
      updateTubeCurve(
        line,
        line.bezierPoints ? line.bezierPoints : line.points,
      );
      dragPoint.position.copy(dragPoint.basePosition);
      dragPoint = null;
      return;
    }

    let index0 = dragPoint.prevPoint.gindex;
    let index2 = dragPoint.nextPoint.gindex;

    if (index1 == index2 || index1 == index0) {
      return;
    }

    let areaPoints = [];
    let oldPoints = [];
    let newPoints = [];
    let oldGeometry = selectedTooth.mesh.geometry.clone();
    let oldAllPoints = getAllBorderPointsArr(selectedTooth);
    let oldAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
    let oldAnchorGroup = getSavedAnchorGroup(selectedTooth);

    dragPoint.basePosition = null;

    let tbPoints = getShortPath(globalGraph, index0, index2);
    tbPoints = tbPoints.slice(1).toReversed().slice(1);

    let oldPoints1 = dragPoint.line2.shortPoints
      ? dragPoint.line2.shortPoints
      : dragPoint.line2.points;
    areaPoints = areaPoints.concat(oldPoints1);
    let oldPoints2 = dragPoint.line1.shortPoints
      ? dragPoint.line1.shortPoints
      : dragPoint.line1.points;
    areaPoints = areaPoints.concat(oldPoints2.slice(1));
    oldPoints = areaPoints;

    let line = dragPoint.line1;
    line.points = line.shortPoints = getShortPath(globalGraph, index1, index2);
    areaPoints = areaPoints.concat(line.points.toReversed().slice(1));
    updateTubeCurve(line, line.points);
    line.point1 = line.points[0];

    dragPoint.position.copy(line.point1);

    line = dragPoint.line2;
    line.points = line.shortPoints = getShortPath(globalGraph, index0, index1);
    areaPoints = areaPoints.concat(line.points.slice(1).toReversed().slice(1));
    updateTubeCurve(line, line.points);
    line.point2 = line.points[line.points.length - 1];

    let mainPoints = getMainPoints(selectedTooth, dragPoint.parent);
    updateBezierPoints(mainPoints, globalGraph);

    newPoints = newPoints.concat(dragPoint.line2.points);
    newPoints = newPoints.concat(dragPoint.line1.points.slice(1));

    // oldPoints.forEach((p) => drawPoint(p, ioViewer.scene, 0xff0000, 0.05));
    // newPoints.forEach((p) => drawPoint(p, ioViewer.scene, 0x0000ff, 0.05));
    // tbPoints.forEach((p) => drawPoint(p, ioViewer.scene, 0xffff00, 0.05));

    let crossPoints = [];
    let newLength = newPoints.length;
    let oldLenth = oldPoints.length;
    oldPoints.forEach((oldPoint, oldIndex) => {
      newPoints.forEach((newPoint, newIndex) => {
        if (
          oldIndex != newIndex &&
          newLength - newIndex != oldLenth - oldIndex &&
          areNearPoints(oldPoint, newPoint)
        ) {
          crossPoints.push(oldPoint);
          // drawPoint(oldPoint, ioViewer.scene, 0x00ff00, 0.05);
          return;
        }
      });
    });
    // console.log("crossPoints", crossPoints.length);

    oldPoints = oldPoints.concat(tbPoints);
    newPoints = newPoints.concat(tbPoints);

    // oldPoints.forEach((p) => drawPoint(p, ioViewer.scene, 0xff0000, 0.05));
    let groupIndex = selectedTooth.pointsGroup.children.indexOf(
      dragPoint.parent,
    );

    let rootPoint = dragPoint.position.clone();
    dragPoint = null;

    document.body.style.cursor = "wait";

    let intersectedPoint = intersectFirst(
      selectedTooth.mesh,
      ioRaycaster,
      rootPoint,
    );
    let isClearing = !!intersectedPoint;
    let model = selectedTooth;
    let scene = ioViewer.scene;
    console.log(
      "endDragPoint isClearing crossPoints",
      isClearing,
      crossPoints.length,
    );

    setTimeout(function () {
      // if (groupIndex <= 0) {
      //   if (crossPoints.length > 0) {
      //     // newPoints.forEach((p) => drawPoint(p, ioViewer.scene, 0x00ff00, 0.05));
      //     fillArea(
      //       model,
      //       oldPoints,
      //       rootPoint,
      //       globalGraph,
      //       scene,
      //       !isClearing
      //     );
      //     fillArea(model, newPoints, rootPoint, globalGraph, scene, isClearing);
      //   } else {
      //     // areaPoints.forEach((p) => drawPoint(p, ioViewer.scene, 0x00ff00, 0.05));
      //     fillArea(
      //       model,
      //       areaPoints,
      //       rootPoint,
      //       globalGraph,
      //       scene,
      //       isClearing
      //     );
      //   }
      // }

      let newGeometry = selectedTooth.mesh.geometry.clone();
      let newAllPoints = getAllBorderPointsArr(selectedTooth);
      let newAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
      let newAnchorGroup = getSavedAnchorGroup(selectedTooth);
      let change = {
        model: selectedTooth,
        oldGeometry: oldGeometry,
        oldPoints: oldAllPoints,
        oldAnchorPoints: oldAnchorPoints,
        oldAnchorGroup: oldAnchorGroup,
        newGeometry: newGeometry,
        newPoints: newAllPoints,
        newAnchorPoints: newAnchorPoints,
        newAnchorGroup: newAnchorGroup,
      };
      addIoChange(change);
      setToothIsChanged(selectedTooth, true);
      selectedTooth.isBrushChanged = false;
      document.body.style.cursor = "default";
    }, 100);
    return;

    setTimeout(function () {
      let allPoints = getAllBorderPoints(selectedTooth);
      let globalMesh = getGlobalMesh();
      let geometry = getFacesGeometry(
        selectedTooth,
        allPoints,
        globalMesh,
        globalGraph,
        ioViewer.scene,
        getCrossedPoints(),
      );
      if (geometry) {
        selectedTooth.mesh.geometry.dispose();
        selectedTooth.mesh.geometry = selectedTooth.mainGeometry = geometry;
      }
      document.body.style.cursor = "default";
    }, 100);
  }
}

//#endregion

//#region Get logic

function getGlobalMesh() {
  return isUpperMode ? upperMesh : lowerMesh;
}

function getGlobalBox() {
  return isUpperMode ? upperBox : lowerBox;
}

function getGlobalGraph() {
  return isUpperMode ? upperGraph : lowerGraph;
}

function getCrossedPoints() {
  return isUpperMode ? upperCrossedPoints : lowerCrossedPoints;
}

function getModelByFileName(fileName, notDeleted) {
  let modelName = fileName
    .replace(GEO_TAG, "")
    .replace(EXTR_TAG, "")
    .replace(STL_TAG, "");
  let model = getModelByName(modelName, notDeleted);
  if (!model) {
    model = allModels.find(
      (m) =>
        (m.filename === fileName ||
          (m.stlModel && m.stlModel.filename === fileName)) &&
        (!notDeleted || !m.isDeleted),
    );
  }
  return model;
}

function getModelByName(name, notDeleted) {
  if (name && allModels.length) {
    return allModels.find(
      (m) => m.name === name && (!notDeleted || !m.isDeleted),
    );
  }
}

function getModelByItem(item, notDeleted) {
  return item && allModels.length
    ? allModels.find(
        (m) => m.listItem === item && (!notDeleted || !m.isDeleted),
      )
    : null;
}

function getToothByUuid(uuid) {
  return uuid ? teeth.find((x) => x.mesh.uuid === uuid && !x.isDeleted) : null;
}

function getToothByNumber(number) {
  return number ? teeth.find((x) => x.number === number && !x.isDeleted) : null;
}

function getNearestMainPoint(newPoint) {
  if (selectedTooth) {
    let npoint = null;
    let points = getMainPoints(selectedTooth);
    let minDistance = Infinity;
    points.forEach((mesh) => {
      let distance = getSquaredDistance(newPoint, mesh.position);
      if (distance < minDistance) {
        minDistance = distance;
        npoint = mesh;
      }
    });
    return npoint;
  }
}

function getSquaredDistance(p1, p2) {
  const dx = p1.x - p2.x,
    dy = p1.y - p2.y,
    dz = p1.z - p2.z;

  return dx * dx + dy * dy + dz * dz;
}

function getAddedToothName() {
  let prefix = "tooth_" + addedSuffix;
  if (isUpperMode) {
    prefix += upperSuffix;
  } else {
    prefix += lowerSuffix;
  }
  for (let i = 1; ; i++) {
    let tempName = prefix + i;
    let existedTooth = teeth.find((t) => t.name === tempName);
    if (!existedTooth) {
      return tempName;
    }
  }
}

//#endregion

//#region Intersect logic

function updateRaycasterFromEvent(event) {
  try {
    let direction = getIoMouse3dDirection(event);
    let origin = ioViewer.camera.position;
    ioRaycaster.set(origin, direction);
  } catch (error) {
    // console.error("updateRaycasterFromEvent", error);
  }
}

function updateIoRaycaster(origin, direction) {
  try {
    ioRaycaster.set(origin, direction);
  } catch (error) {
    console.error("updateRaycaster", error);
  }
}

function getIoMouse3dDirection(event) {
  let direction = null;
  if (event && event.pageX && event.pageY) {
    let intraoralContainer = document.getElementById("intraoralContainer");
    let overlayRect = intraoralContainer.getBoundingClientRect();
    let x = event.pageX - overlayRect.left;
    let y = event.pageY - overlayRect.top;
    direction = new THREE.Vector3(
      (x / overlayRect.width) * 2 - 1,
      -(y / overlayRect.height) * 2 + 1,
      0.5,
    );
    direction.unproject(ioViewer.camera);
    direction.sub(ioViewer.camera.position);
    direction.normalize();
  }
  return direction;
}

function getAllMeshesFromGroup(pointsGroup, borderGroup) {
  let meshes = [];
  for (let childGroup of pointsGroup.children) {
    meshes.push(...childGroup.children);
  }
  return typeof borderGroup !== "undefined"
    ? meshes.filter((m) => m.parent === borderGroup)
    : meshes;
}

function getPointMeshesFromGroup(pointsGroup, borderGroup) {
  let meshes = [];
  for (let childGroup of pointsGroup.children) {
    meshes.push(...childGroup.children.filter((item) => item.name === "point"));
  }
  return typeof borderGroup !== "undefined"
    ? meshes.filter((m) => m.parent === borderGroup)
    : meshes;
}

function getLineMeshesFromGroup(pointsGroup, borderGroup) {
  let meshes = [];
  for (let childGroup of pointsGroup.children) {
    meshes.push(...childGroup.children.filter((item) => item.name != "point"));
  }
  return typeof borderGroup !== "undefined"
    ? meshes.filter((m) => m.parent === borderGroup)
    : meshes;
}

function intersectPoint(pointsGroup) {
  let pointMesh = null;
  if (pointsGroup) {
    let meshes = getPointMeshesFromGroup(pointsGroup);
    // pointsGroup.children.filter((item) => item.name === "point");
    let globalMesh = getGlobalMesh();
    // meshes.push(globalMesh);
    let intersects = ioRaycaster.intersectObjects(meshes);
    if (intersects.length > 0) {
      let firstMesh = intersects[0].object;
      // console.log(firstMesh.name);
      if (firstMesh != globalMesh) {
        pointMesh = firstMesh;
      }
    }
  }
  return pointMesh;
}

function intersectPointsGroup(pointsGroup) {
  let pointMesh = null;
  if (pointsGroup) {
    let meshes = getAllMeshesFromGroup(pointsGroup);
    let globalMesh = getGlobalMesh();
    // meshes.push(globalMesh);
    let intersects = ioRaycaster.intersectObjects(meshes);
    if (intersects.length > 0) {
      let firstMesh = intersects[0].object;
      // console.log(firstMesh.name);
      if (firstMesh != globalMesh) {
        pointMesh = firstMesh;
      }
    }
  }
  return pointMesh;
}

function intersectTooth(raycaster) {
  if (!raycaster) {
    raycaster = ioRaycaster;
  }

  let tooth = null;
  let tMeshes = teeth.map((t) => t.mesh).filter((m) => m.geometry);
  // if (viewMode.isEdit && viewMode.isBrushMode && selectedTooth) {
  //   let globalMesh = getGlobalMesh();
  //   if (globalMesh && globalMesh.highlightMesh) {
  //     tMeshes.push(globalMesh.highlightMesh);
  //   }
  // }

  let intersects = raycaster.intersectObjects(tMeshes); // ioViewer.scene.children);
  if (intersects.length > 0) {
    intersects.find((intersect) => {
      let objectUuid = intersect.object.uuid;
      let tempTooth = getToothByUuid(objectUuid);
      if (tempTooth && !tempTooth.isDeleted) {
        if (tempTooth.mesh.visible || tempTooth == selectedTooth)
          tooth = tempTooth;
      }
      return tooth != null;
    });
  }
  return tooth;
}

function intersectScene() {
  let point = null;
  let intersects = ioRaycaster.intersectObject(getGlobalMesh());
  if (intersects.length > 0) {
    point = intersects[0].point;
  }
  return point;
}

function intersectSelectedBorder() {
  let lineMesh = null;

  if (
    selectedTooth &&
    selectedTooth.pointsGroup &&
    selectedTooth.pointsGroup.visible
  ) {
    let meshes = getLineMeshesFromGroup(selectedTooth.pointsGroup);
    let intersects = ioRaycaster.intersectObjects(meshes);

    if (intersects.length > 0) {
      // point = intersects[0].point;
      // drawPoint(point, ioViewer.scene);
      return intersects[0].object;
    }
  }

  return lineMesh;
}

function intersectMesh(mesh, raycaster) {
  let point = null;
  if (!raycaster) {
    raycaster = ioRaycaster;
  }
  if (mesh) {
    let intersects = raycaster.intersectObject(mesh);
    if (intersects.length > 0) {
      point = intersects[0].point;
    }
  }
  return point;
}

function intersectFirst(mesh, raycaster, targetPoint) {
  let point = null;
  if (!raycaster) {
    raycaster = ioRaycaster;
  }
  if (mesh) {
    let intersects = raycaster.intersectObject(mesh);
    // let globalMesh = getGlobalMesh();
    //.intersectObjects([mesh, globalMesh]);
    for (let intersect of intersects) {
      let dist = getDistance(targetPoint, intersect.point);
      if (dist < 0.999) {
        point = intersect.point;
      }
    }
  }
  return point;
}

//#endregion

//#region Points

const minDistance = 2;

function createPointsGroupFromBorders(model, bordersGroup) {
  console.log("createPointsGroupFromBorders", model, bordersGroup);
  if (!model) return null;

  let globalGraph = getGlobalGraph();
  if (model && bordersGroup && globalGraph) {
    if (model.pointsGroup) {
      ioViewer.scene.remove(model.pointsGroup);
    }

    const modelCameraDistance = getModelCameraDistance(model);
    model.pointsGroup = new THREE.Group();
    model.pointsGroup.visible = false;
    model.mainPoints = [];

    let anchorGroup = [];
    for (let points of bordersGroup) {
      const anchorPoints = null;
      const newAnchorPoints = [];
      const groupMainPoints = [];

      if (points.length > 1) {
        const group = new THREE.Group();
        let distance = 0;
        let indexDif = 0;
        points.forEach((ppp, index) => {
          if (index != 0) {
            distance += getDistance(points[index - 1], points[index]);
            indexDif++;
          }
          if (index == points.length - 1) {
            distance = minDistance;
          }
          let adistance =
            anchorPoints && anchorPoints.length
              ? getDistance(anchorPoints[0], points[index])
              : 0;
          if (
            anchorPoints &&
            !anchorPoints.length &&
            index != points.length - 1
          )
            return;

          if (
            (anchorPoints && adistance < 0.01) ||
            (!anchorPoints && distance >= minDistance)
          ) {
            if (anchorPoints) anchorPoints.shift();
            let subset = points.slice(index - indexDif, index + 1);
            distance = 0;
            indexDif = 0;
            let point1 = subset[0];

            if (index == points.length - 1) {
              subset.push(points[0]);
            }

            let point2 = subset[subset.length - 1];

            if (subset.length < 2) {
              console.log("wrong subset");
              return;
            }

            let line = createTubeCurve(subset, modelCameraDistance);
            line.point1 = point1;
            line.point2 = point2;
            line.points = subset;

            let index1 = getPointIndex(globalGraph, point1);
            let index2 = getPointIndex(globalGraph, point2);
            let tempSubset = getShortPath(globalGraph, index1, index2);
            if (tempSubset.length > 1) {
              line.shortPoints = tempSubset;
              line.bezierPoints = tempSubset;
              subset = tempSubset;
              point1 = subset[0];
              point2 = subset[subset.length - 1];
            }

            let point = createIoPoint(point1, modelCameraDistance);
            newAnchorPoints.push([point1.x, point1.y, point1.z]);

            point.line1 = line;
            point.line2 = group.children.slice(-1)[0];
            groupMainPoints.push(point);

            group.add(point, line);
          }
        });
        group.children[0].line2 = group.children.slice(-1)[0];
        model.pointsGroup.add(group);
        updateBezierPoints(groupMainPoints, globalGraph);

        // console.log("newAnchorPoints", newAnchorPoints);
        model.anchorPoints = model.savedAnchorPoints = newAnchorPoints;
        anchorGroup.push(newAnchorPoints);
      } else {
        console.log("invalid points for model", model);
      }
    }

    model.anchorGroup = model.savedAnchorGroup = anchorGroup;
    ioViewer.scene.add(model.pointsGroup);
    return model.pointsGroup;
  }
}

function createPointsGroupBase(model) {
  if (!model) return null;
  let globalGraph = getGlobalGraph();
  if (!model.pointsGroup && model.points && globalGraph) {
    const modelCameraDistance = getModelCameraDistance(model);

    model.pointsGroup = new THREE.Group();
    model.pointsGroup.visible = false;

    const group = new THREE.Group();
    model.pointsGroup.add(group);
    model.mainPoints = [];

    const points = model.points.map((p) =>
      p.isVector3 ? p : new THREE.Vector3(p[0], p[1], p[2] + globalDz),
    );

    const anchorPoints = null;
    const newAnchorPoints = [];

    if (points.length > 1) {
      let distance = 0;
      let indexDif = 0;
      points.forEach((ppp, index) => {
        if (index != 0) {
          distance += getDistance(points[index - 1], points[index]);
          indexDif++;
        }
        if (index == points.length - 1) {
          distance = minDistance;
        }
        let adistance =
          anchorPoints && anchorPoints.length
            ? getDistance(anchorPoints[0], points[index])
            : 0;
        if (anchorPoints && !anchorPoints.length && index != points.length - 1)
          return;

        if (
          (anchorPoints && adistance < 0.01) ||
          (!anchorPoints && distance >= minDistance)
        ) {
          if (anchorPoints) anchorPoints.shift();
          let subset = points.slice(index - indexDif, index + 1);
          distance = 0;
          indexDif = 0;
          let point1 = subset[0];

          if (index == points.length - 1) {
            subset.push(points[0]);
          }

          let point2 = subset[subset.length - 1];

          if (subset.length < 2) {
            console.log("wrong subset");
            return;
          }

          let line = createTubeCurve(subset, modelCameraDistance);
          line.point1 = point1;
          line.point2 = point2;
          line.points = subset;

          let index1 = getPointIndex(globalGraph, point1);
          let index2 = getPointIndex(globalGraph, point2);
          let tempSubset = getShortPath(globalGraph, index1, index2);
          if (tempSubset.length > 1) {
            line.shortPoints = tempSubset;
            line.bezierPoints = tempSubset;
            subset = tempSubset;
            point1 = subset[0];
            point2 = subset[subset.length - 1];
          }

          let color = 0x00ff00;
          // if (index === 0) {
          //   color = 0xff0000;
          // }
          // if (index === 10) {
          //   color = 0x0000ff;
          // }

          let point = createIoPoint(point1, modelCameraDistance);
          newAnchorPoints.push([point1.x, point1.y, point1.z]);

          point.line1 = line;
          point.line2 = group.children.slice(-1)[0];
          model.mainPoints.push(point);

          group.add(point, line);
        }
      });
      group.children[0].line2 = group.children.slice(-1)[0];
      console.log("newAnchorPoints", newAnchorPoints);
      model.anchorPoints = model.savedAnchorPoints = newAnchorPoints;
      model.anchorGroup = model.savedAnchorGroup = [newAnchorPoints];

      updateBezierPoints(model.mainPoints, globalGraph);
    } else {
      console.log("invalid points for model", model);
    }

    model.pointsGroup.visible = false;
    ioViewer.scene.add(model.pointsGroup);
  }
  return model.pointsGroup;
}

function getModelCameraDistance(model) {
  let modelCameraDistance = 0;
  if (ioViewer && ioViewer.camera && model.defaultBox) {
    modelCameraDistance = getDistance(
      ioViewer.camera.position,
      model.defaultBox.center,
    );
  }
  return modelCameraDistance;
}

function createPointsGroup(model) {
  if (!model) return null;

  const globalGraph = getGlobalGraph();
  if (!model.pointsGroup && globalGraph) {
    if (!model.anchorPoints || !model.anchorGroup) {
      //  || !vueApp.isEngineerMode
      return createPointsGroupBase(model);
    }

    const modelCameraDistance = getModelCameraDistance(model);
    model.pointsGroup = new THREE.Group();
    model.pointsGroup.visible = false;

    let i = 0;
    let anchorGroup = model.savedAnchorGroup
      ? model.savedAnchorGroup
      : model.anchorGroup;
    for (let grPoints of anchorGroup) {
      if (grPoints.length < 3) {
        continue;
      }
      const group = new THREE.Group();
      model.pointsGroup.add(group);

      const groupMainPoints = [];
      const anchorPoints = grPoints.map(
        (p) => new THREE.Vector3(p[0], p[1], p[2] + globalDz),
      );
      // let firstAnchor = anchorPoints ? anchorPoints.shift() : null;

      if (anchorPoints.length > 1) {
        anchorPoints.forEach((ppp, index) => {
          let point1 = anchorPoints[index];
          let point2 = anchorPoints[index + 1];
          if (!point2) {
            point2 = anchorPoints[0];
          }
          let anchorDistance = getDistance(point1, point2);
          // console.log("anchorDistance", anchorDistance);

          let index1 = getPointIndex(globalGraph, point1);
          let index2 = getPointIndex(globalGraph, point2);
          let subset = getShortPath(globalGraph, index1, index2);
          if (subset.length > 1) {
            // let tubeRadius = anchorDistance < 0.7 ? 0.025 : 0.05;
            let line = createTubeCurve(subset, modelCameraDistance);
            point1 = subset[0];
            point2 = subset[subset.length - 1];
            line.point1 = point1;
            line.point2 = point2;
            line.points = subset;
            // line.tubeRadius = tubeRadius;

            line.shortPoints = subset;
            line.bezierPoints = subset;

            // let color = 0x00ff00;
            // let pointRadius = anchorDistance < 0.7 ? 0.05 : 0.1;
            let point = createIoPoint(point1, modelCameraDistance);
            point.line1 = line;
            point.line2 = group.children.slice(-1)[0];
            groupMainPoints.push(point);

            group.add(point, line);
          }
        });
        group.children[0].line2 = group.children.slice(-1)[0];

        updateBezierPoints(groupMainPoints, globalGraph);
      } else {
        console.log("invalid points for model", model);
      }
      i++;
    }
    ioViewer.scene.add(model.pointsGroup);
  }
  return model.pointsGroup;
}

function setNextPrevPoints(point, points) {
  if (point.parent) points = points.filter((p) => p.parent == point.parent);

  let pindex = points.indexOf(point);
  point.prevPoint = points[pindex - 1];
  if (!point.prevPoint) {
    point.prevPoint = points[points.length - 1];
  }
  point.nextPoint = points[pindex + 1];
  if (!point.nextPoint) {
    point.nextPoint = points[0];
  }
  let globalGraph = getGlobalGraph();
  point.prevPoint.gindex = getPointIndex(globalGraph, point.prevPoint.position);
  point.nextPoint.gindex = getPointIndex(globalGraph, point.nextPoint.position);
}

function createLine(prevPoint, nextPoint, modelCameraDistance) {
  let globalGraph = getGlobalGraph();
  let index0 = prevPoint.gindex;
  let index1 = nextPoint.gindex;
  let pathPoints = getShortPath(globalGraph, index0, index1);
  let line = createTubeCurve(pathPoints, modelCameraDistance);
  line.point1 = prevPoint.position;
  line.point2 = nextPoint.position;
  line.points = line.shortPoints = pathPoints;
  prevPoint.line1 = line;
  nextPoint.line2 = line;
  return line;
}

function getAllBorderPoints(tooth) {
  let allPoints = [];
  if (tooth) {
    if (!tooth.pointsGroup) createPointsGroup(tooth);
    getLineMeshesFromGroup(tooth.pointsGroup).forEach((mesh) => {
      if (mesh.name != "point") {
        let line = mesh;
        let linePoints = line.shortPoints ? line.shortPoints : line.points;
        //line.bezierPoints ? line.bezierPoints : line.points;
        allPoints.push(...linePoints.slice(1));
      }
    });
  }
  return allPoints;
}

function getAllBorderPointsArr(tooth) {
  let allPoints = [];
  if (tooth && (tooth.points || tooth.pointsGroup)) {
    if (!tooth.pointsGroup) {
      allPoints = tooth.points;
    } else {
      getLineMeshesFromGroup(tooth.pointsGroup).forEach((mesh) => {
        if (mesh.name != "point") {
          let line = mesh;
          let linePoints = line.shortPoints ? line.shortPoints : line.points;
          allPoints.push(...linePoints.slice(1).map((p) => [p.x, p.y, p.z]));
        }
      });
    }
  }
  return allPoints;
}

function getSavedBorderPointsArr(tooth) {
  let allPoints = [];
  if (tooth && (tooth.points || tooth.pointsGroup)) {
    if (!tooth.pointsGroup) {
      allPoints = tooth.points;
    } else {
      getLineMeshesFromGroup(tooth.pointsGroup).forEach((mesh) => {
        if (mesh.name != "point") {
          let line = mesh;
          let linePoints = line.bezierPoints ? line.bezierPoints : line.points;
          allPoints.push(...linePoints.slice(1).map((p) => [p.x, p.y, p.z]));
        }
      });
    }
  }
  return allPoints;
}

function getSavedAnchorPointsArr(tooth) {
  let aPoints = [];
  if (tooth && (tooth.points || tooth.pointsGroup)) {
    if (!tooth.pointsGroup) {
      aPoints = tooth.anchorPoints;
    } else {
      getPointMeshesFromGroup(tooth.pointsGroup).forEach((mesh) => {
        if (mesh.name == "point") {
          let p = mesh.basePosition ? mesh.basePosition : mesh.position;
          aPoints.push([p.x, p.y, p.z]);
        }
      });
    }
  }
  return aPoints;
}

function getSavedAnchorGroup(tooth) {
  let anchorGroup = null;
  if (tooth && (tooth.anchorGroup || tooth.pointsGroup)) {
    if (!tooth.pointsGroup) {
      anchorGroup = tooth.anchorGroup;
    } else {
      anchorGroup = [];
      for (let childGroup of tooth.pointsGroup.children) {
        let points = childGroup.children.filter(
          (item) => item.name === "point",
        );
        let aPoints = [];
        points.forEach((mesh) => {
          let p = mesh.basePosition ? mesh.basePosition : mesh.position;
          aPoints.push([p.x, p.y, p.z]);
        });
        anchorGroup.push(aPoints);
      }
    }
  }
  return anchorGroup;
}

function getMainPoints(tooth, borderGroup) {
  if (tooth) {
    if (!tooth.pointsGroup) createPointsGroup(tooth);
    let mainPoints = getPointMeshesFromGroup(tooth.pointsGroup, borderGroup);
    return mainPoints;
  }
}

function smoothSelectedToothBorder() {
  if (selectedTooth) {
    document.body.style.cursor = "wait";
    document.getElementById("intraoralSmoothBtn").style.cursor = "wait";

    let model = selectedTooth;
    setTimeout(() => {
      let mainPoints = getMainPoints(model);

      let globalGraph = getGlobalGraph();
      mainPoints.forEach((p1, index) => {
        let p2 = mainPoints[index + 1];
        if (!p2) p2 = mainPoints[0];
        let index1 = getPointIndex(globalGraph, p1.position);
        let index2 = getPointIndex(globalGraph, p2.position);
        let points = getShortPath(globalGraph, index1, index2);
        let line = p1.line1;
        line.point1 = p1;
        line.point2 = p2;
        line.points = line.shortPoints = points;
        updateTubeCurve(line, points);
      });

      document.body.style.cursor = "default";
      document.getElementById("intraoralSmoothBtn").style.cursor = "pointer";
    }, 100);
  }
}

function repairIntraoralColouring() {
  if (selectedTooth) {
    let repairBtn = document.getElementById("intraoralRepairBtn");
    document.body.style.cursor = "wait";
    if (repairBtn) repairBtn.style.cursor = "wait";

    let model = selectedTooth;
    let jsonData = {};
    let sPoints = getSavedBorderPointsArr(model);
    let allPoints = getAllBorderPointsArr(model);
    jsonData[model.name] = sPoints;
    jsonData["teeth"] = [model.name];
    if (model.crossedTeeth && model.crossedTeeth.length) {
      jsonData["crossedTeeth"] = model.crossedTeeth;
    } else {
      jsonData["crossedTeeth"] = [];
      // allModels.filter(m => m.isUpper == isUpperMode && m.isTooth && !m.isAdded).map(m => m.name);
      // && !m.isDeleted
    }

    const jsonBlob = new Blob([JSON.stringify(jsonData)], {
      type: "application/json",
    });
    sendApplyBorderPointsRequest(
      jsonBlob,
      upperMesh && lowerMesh,
      model.isUpper,
      (zipBlob) => {
        document.body.style.cursor = "default";
        if (repairBtn) repairBtn.style.cursor = "pointer";
        applyZipBlob(zipBlob);
      },
    );
  }
}

function updateOpacity(opacityValue) {
  viewMode.opacity = opacityValue;
  allModels.forEach((model) => {
    ioViewer.set_opacity(model.id, opacityValue);
  });
}

//#endregion

//#region Modes/Camera

async function switchViewModes(isUpper, isLower) {
  console.log("switchViewModes", isUpper, isLower);

  if (viewMode.isOriginalView) {
    await switchOriginalView(viewMode.isOriginalView);
    return;
  }

  if (isLower && !lowerMesh) {
    await loadLower();
  }

  let previosDouble = isDoubleMode;

  isDoubleMode = isUpper && isLower;
  if (isDoubleMode || isUpperMode != isUpper) {
    await unfocusAny();
  }

  isEditMode = viewMode.isEdit && !isDoubleMode && !vueApp.isViewMode;
  if (isUpperMode != isUpper || !isEditMode) {
    updateSelectedTooth(null);
    isUpperMode = isUpper;
    selectedTooth = null;
    await switchMeasureMode(viewMode.isMeasureMode);
    await switchArchMode(viewMode.isArchMode);
  }
  allModels.forEach((model) => {
    model.mesh.visible =
      model.isVisible &&
      !model.isDeleted &&
      (model.isUpper === isUpper || isDoubleMode);
    if (model.pointsGroup) {
      model.pointsGroup.visible = false;
      // model.mesh.material.polygonOffsetFactor = 3;
    }
  });
  resetIoCamera();

  if (isUpperMode || viewMode.isMeasureMode || viewMode.isArchMode)
    rotateIntraoralToView(2);

  if (previosDouble != isDoubleMode) {
    let scaleMatrix = new THREE.Matrix4().makeScale(1, 1, -1);
    let scaleInvert = scaleMatrix.clone().invert();
    let translationMatrix = new THREE.Matrix4().makeTranslation(0, 0, 3);
    let translationInvert = translationMatrix.clone().invert();

    allModels.forEach((model) => {
      if (ioUpperMatrix && ioLowerMatrix) {
        // if (!model.isUpper) {
        //   model.mesh.applyMatrix4(
        //     isDoubleMode ? ioLowerMatrix : ioLowerMatrixInverted
        //   );
        // }
        if (model.isUpper) {
          model.mesh.applyMatrix4(
            isDoubleMode ? ioUpperMatrix : ioUpperMatrixInverted,
          );
        }
      } else {
        if (model.isUpper) {
          if (isDoubleMode) {
            model.mesh.applyMatrix4(scaleMatrix);
            model.mesh.applyMatrix4(translationMatrix);
          } else {
            model.mesh.applyMatrix4(translationInvert);
            model.mesh.applyMatrix4(scaleInvert);
          }
        }
      }
    });

    if (isDoubleMode) {
      let point = defaultIoBox.center;
      ioViewer.camera.position.x = point.x;
      ioViewer.camera.position.y = -defaultIoZoom;
      ioViewer.camera.position.z = point.z * 0.8;

      ioViewer.camera.up.y = 0;
      ioViewer.camera.up.x = 0;
      ioViewer.camera.up.z = 1;
    }
  }
  ioViewer.camera.updateProjectionMatrix();
  ioViewer.controls.update();

  await switchTeethNumbers(isDoubleMode ? false : viewMode.showTeethNumbers);
}

async function checkIsEditMode(model) {
  if (!isEditMode) {
    let willEdit = await swal({
      text: "Cannot edit in this mode. Do you want to switch to edit mode?",
      buttons: {
        yes: { text: "Yes", value: true },
        no: { text: "No", value: false },
      },
    });

    if (willEdit) {
      viewMode.isEdit = true;

      if (viewMode.isOriginalView) {
        await switchOriginalView(false);
      }

      leaveDoubleMode(model);
      await switchEditMode(viewMode.isEdit);
    }
  }
  return isEditMode;
}

function isAnyToothFocused() {
  return teeth.find((t) => t.isFocused || (t.listItem && t.listItem.isFocused));
}

function isAnyModelFocused() {
  return allModels.find(
    (t) => t.isFocused || (t.listItem && t.listItem.isFocused),
  );
}

function resetIoCamera(isZoom2) {
  // let point = { x: 0, y: 0, z: 0 };
  let point = defaultIoBox.center;
  // console.log("resetIoCamera", point);
  ioViewer.camera.lookAt(point.x, point.y, point.z);
  ioViewer.controls.target = new THREE.Vector3(point.x, point.y, point.z);

  ioViewer.camera.up.y = 1;
  ioViewer.camera.up.x = 0;
  ioViewer.camera.up.z = 0;
  ioViewer.camera.position.x = 0; //point.x;
  ioViewer.camera.position.y = 0; //point.y;
  ioViewer.camera.position.z = isZoom2 ? defaultIoZoom2 : defaultIoZoom;
}

async function rotateIntraoralToView(viewNumber, clearFocus) {
  if (viewMode.isOriginalView) return;
  mouseCameraPoint = null;
  // console.log("rotateIntraoralToView", viewNumber, ioViewer.camera);
  let focusedItem = allItems.find((t) => t.isFocused);
  if (focusedItem && clearFocus) {
    focusedItem.isFocused = false;
    await switchItemFocus(focusedItem);
  }

  resetIoCamera(viewNumber === 1 || viewNumber === 2);
  let viewQuarterinion1 = null;
  let viewQuarterinion2 = null;
  switch (viewNumber) {
    case 1:
      break;
    case 2:
      viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(0, 0, 1), // z-axis
        degreesToRadians(180),
      );
      break;
    case 3:
      if (isUpperMode) {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(-90),
        );
        viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 0, 1),
          degreesToRadians(180),
        );
        // viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
        //   new THREE.Vector3(0, 1, 0),
        //   degreesToRadians(180)
        // );
        // viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
        //   new THREE.Vector3(1, 0, 0),
        //   degreesToRadians(-90)
        // );
      } else {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(90),
        );
      }
      break;
    case 4:
      if (isUpperMode) {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(-90),
        );
      } else {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(-90),
        );
        viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 1, 0),
          degreesToRadians(180),
        );
      }

      break;
    case 5:
      if (isUpperMode) {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(-90),
        );
        viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 0, 1),
          degreesToRadians(-90),
        );
      } else {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(90),
        );
        viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 0, 1),
          degreesToRadians(-90),
        );
      }
      break;
    case 6:
      if (isUpperMode) {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(-90),
        );
        viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 0, 1),
          degreesToRadians(90),
        );
      } else {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(90),
        );
        viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 0, 1),
          degreesToRadians(90),
        );
      }
      break;
  }

  if (viewQuarterinion1) {
    ioViewer.camera.up.applyQuaternion(viewQuarterinion1);
    ioViewer.camera.position.applyQuaternion(viewQuarterinion1);
  }

  if (viewQuarterinion2) {
    ioViewer.camera.up.applyQuaternion(viewQuarterinion2);
    ioViewer.camera.position.applyQuaternion(viewQuarterinion2);
  }

  ioViewer.camera.updateProjectionMatrix();
  ioViewer.controls.update();
  ioViewer.controls.dispatchChangeAfterDelay(100);
}

function setRotateCenter() {
  console.log("setRotateCenter");
  let centerPoint = null;

  let focusedItem = allItems.find((t) => t.isFocused);
  let model = getModelByItem(focusedItem);
  if (!model && selectedTooth) model = selectedTooth;
  if (model && model.mesh) {
    let box = model.defaultBox;
    if (!box && model.mesh.parent) {
      model.mesh.geometry.computeBoundingBox();
      box = model.mesh.geometry.boundingBox;
      box = offsetBox(box);
    }
    if (box && box.size.x > 0 && box.size.y > 0 && box.size.z > 0) {
      let point = box.center;
      centerPoint = new THREE.Vector3(point.x, point.y, point.z);
    }
  }
  if (!centerPoint) {
    let point = defaultIoBox.center;
    centerPoint = new THREE.Vector3(point.x, point.y, point.z);
    // if (mouseCameraPoint) {
    //   centerPoint = mouseCameraPoint;
    // }
  }
  if (centerPoint) {
    ioViewer.camera.lookAt(centerPoint);
    ioViewer.camera.updateProjectionMatrix();
    ioViewer.controls.target.copy(centerPoint);
    ioViewer.controls.update();
    ioViewer.controls.dispatchChangeAfterDelay(100);
  }
}

function changeTeethColor(teethColor) {
  console.log("changeTeethColor", teethColor);
  selectedTeethColor = teethColor;
  teeth.forEach((tooth) => {
    if (teethColor == randomColorValue) {
      let color = getColorFromToothNumber(tooth.number);
      ioViewer.set_color(tooth.id, color);
    } else if (teethColor == monochromeColorValue) {
      ioViewer.set_color(tooth.id, greyColorValue);
    } else {
      ioViewer.set_color(tooth.id, teethColor);
    }

    tooth.mesh.visible =
      !tooth.isDeleted &&
      !tooth.listItem.isHidden &&
      tooth.isVisible &&
      (tooth.isUpper === isUpperMode || isDoubleMode);
  });

  let gingivas = allModels.filter((model) => model.isGingiva);
  gingivas.forEach((model) => {
    if (teethColor == monochromeColorValue) {
      ioViewer.set_color(model.id, greyColorValue);
    } else {
      ioViewer.set_color(model.id, gingivaColor);
    }

    model.mesh.visible =
      !model.listItem.isHidden &&
      model.isVisible &&
      (model.isUpper === isUpperMode || isDoubleMode);
  });
}

async function switchTeethNumbers(showTeethNumbers) {
  viewMode.showTeethNumbers = showTeethNumbers;
  if (viewMode.showTeethNumbers) {
    // rotateIntraoralToView(3);
    // if (isUpperMode) rotateIntraoralToView(2);
    // else rotateIntraoralToView(1);
    showIntraoralLoader();

    removeTeethNumbers();
    await delay(200);
    drawTeethNumbers(isUpperMode);
    hideLoader();
  } else {
    removeTeethNumbers();
  }
}

async function switchEditMode(isEdit) {
  isEditMode = viewMode.isEdit = isEdit;
  if (!isEditMode) {
    await switchToContourMode();
    updateSelectedTooth(null);
    intraoralMenu.style.display = "none";
    dragPoint = null;
    intersectedTooth = null;
    ioViewer.controls.enabled = true;
    teeth.forEach((model) => {
      if (model.pointsGroup) {
        model.pointsGroup.visible = false;
      }
    });
    cancelAdd();
    $(".intraoral-edit-item").css("display", "none");
  } else {
    await switchMeasureMode(false);
    vueApp.leaveModes();
    await unfocusAny();
    await loadEditData();

    $(".intraoral-edit-item").css("display", "block");
  }
}

async function enterIntraoralOrtho() {
  await resetIoView();
  let anyFocused = isAnyModelFocused();
  gingivae.forEach((xmodel) => {
    if (xmodel) {
      xmodel.mainMesh.visible = true;
      xmodel.filledMesh.visible = false;
      xmodel.mesh = xmodel.mainMesh;
      xmodel.isVisible = xmodel.listItem.isVisible = true;
      xmodel.mesh.visible =
        xmodel.isUpper == isUpperMode &&
        (!anyFocused || xmodel.listItem.isFocused);
    }
  });
}

async function removeBraces() {
  // console.log("removeBraces");
  let inProgress = await checkBracesTaskInProgress(vueApp.patientId);
  if (inProgress) {
    swal({
      text: "Remove braces task is already running.",
    });
  } else {
    swal({
      text: "This action will remove braces and reprocess the crowns. All previous changes will be lost.",
      buttons: {
        yes: { text: "Confirm", value: true },
        no: { text: "Cancel", value: false },
      },
    }).then(async (willDelete) => {
      if (willDelete) {
        console.log(`removeBraces patient ${vueApp.patientId}`);
        await sendRemoveBracesRequest();
      }
    });
  }
}

//#endregion

//#region Panoramic

// async function getPanoramicImgUrl() {
//   let panoramicImgUrl = null;
//   try {
//     const requestUrl = `https://qa.ceph-x.com/cephx/images/panoramicForRootsLink/${vueApp.patientId}`;
//     const response = await fetch(requestUrl);
//     panoramicImgUrl = await response.text();
//   } catch (error) {
//     console.log("getPanoramicImgUrl ERROR", error);
//   }
//   return;
// }

async function switchPanoramicRoots(newValue) {
  viewMode.isPanoramicRoots = newValue;
  if (viewMode.isPanoramicRoots) {
    if (!panoramicRootsLoaded) {
      panoramicRootsLoaded = true;
      showIntraoralLoader();
      let firstProgress = true;
      let promise = new JSZip.external.Promise(function (resolve, reject) {
        JSZipUtils.getBinaryContent(vueApp.intraoralPanoramicZipUrl, {
          progress: function (event) {
            if (firstProgress) {
              $("#loaderText").css("margin-left", "10px");
              firstProgress = false;
            }
            let percent = Math.ceil(event.percent * 0.75);
            $("#loaderText").html(`${percent} %`);
          },
          callback: function (err, data) {
            stlLogMessage(`${new Date()} zip downloaded`);
            if (err) {
              reject(err);
            } else {
              $("#loaderText2").html(`Loading files...`);
              $("#loaderText3").html(`just a few more seconds`);
              resolve(JSZip.loadAsync(data));
            }
          },
        });
      });
      let zip = await promise;
      if (zip) {
        console.log("switchPanoramicRoots zip", zip);
        for (let [filename, file] of Object.entries(zip.files)) {
          let tFileName = filename.replace("rooted_", "");
          let existedTooth = allModels.find((x) => x.filename === tFileName);
          if (existedTooth) {
            let zipFile = zip.file(filename);
            let buffer = await zipFile.async("arraybuffer");
            let panoramicGeometry = getGeometryFromStl(buffer);
            existedTooth.panoramicGeometry = panoramicGeometry;
          }
        }
      }

      setPanoPanelItems();

      let panoramicImgUrl = await getPanoramicImgUrl(vueApp.patientId);
      if (panoramicImgUrl) {
        document.getElementById("ioPanoramicImg").src = panoramicImgUrl;
        viewMode.showPanoramicImg = true;
        document.getElementById("ioPanoramicViewer").style.display = "block";
        ioViewer.do_resize();

        let img = new Image();
        img.onload = async function () {
          const sceneWidth = img.width;
          const sceneHeight = img.height;
          console.log("img onload ", sceneWidth, sceneHeight);
          const ioDiv = document.getElementById("ioPanoramicDiv");
          ioDiv.style.width = `${sceneWidth}px`;
          ioDiv.style.height = `${sceneHeight}px`;
          const stage = (panoKonvaStage = new Konva.Stage({
            container: "ioPanoramicDiv",
            width: sceneWidth,
            height: sceneHeight,
          }));

          const layer = (panoKonvaLayer = new Konva.Layer());
          stage.add(layer);
          try {
            await loadPanoramicMasks(layer);
          } catch (error) {
            console.error(error);
          }

          // Function to make the stage responsive
          function fitStageIntoParentContainer() {
            const container = document.getElementById("ioPanoramicViewer");
            const containerWidth = container.offsetWidth;
            const ioDiv = document.getElementById("ioPanoramicDiv");
            ioDiv.style.width = `${container.offsetWidth}px`;
            ioDiv.style.height = `${
              (containerWidth * sceneHeight) / sceneWidth
            }px`;

            // Calculate scale based on virtual width vs actual width
            const scale = containerWidth / sceneWidth;
            console.log("ioPanoramicDiv scale", containerWidth, scale);

            // Set stage dimensions and scale
            stage.width(sceneWidth * scale);
            stage.height(sceneHeight * scale);
            stage.scale({ x: scale, y: scale });
          }

          fitStageIntoParentContainer();
          window.addEventListener("resize", fitStageIntoParentContainer);

          hideLoader();
        };
        img.onerror = function () {
          console.log("img onerror ", img);
          hideLoader();
        };
        img.src = panoramicImgUrl;
      } else {
        hideLoader();
      }
    }

    if (viewMode.showPanoramicImg) {
      document.getElementById("ioPanoramicViewer").style.display = "block";
      ioViewer.do_resize();
    }

    for (const tooth of teeth) {
      if (tooth.panoramicGeometry) {
        tooth.mesh.geometry = tooth.panoramicGeometry;
      }
    }
  } else {
    document.getElementById("ioPanoramicViewer").style.display = "none";
    ioViewer.do_resize();
    for (const tooth of teeth) {
      if (tooth.mainGeometry) {
        tooth.mesh.geometry = tooth.mainGeometry;
      }
    }
  }
}

function setPanoPanelItems() {
  for (const tooth of teeth) {
    let panoPanelItem = panoPanelItems.find((f) => f.id == tooth.number);
    if (panoPanelItem) {
      panoPanelItem.item = tooth.listItem;
      panoPanelItem.icon_color = "blue";
    }
  }
}

async function loadPanoramicMasks() {
  let overlayJson =
    //"demo/panoramic-overlay.json";
    `${vueApp.stlBaseUrl}/panoramic-overlay.json`;
  const response = await fetch(overlayJson);
  if (!response.ok) return;

  const data = (panoJsonData = await response.json());
  const imgHeight = data.instance_segmentation.full_image_size_hw[0];
  const imgWidth = data.instance_segmentation.full_image_size_hw[1];
  const maskX = data.instance_segmentation.stage2_crop_origin_xy[0];
  const maskY = data.instance_segmentation.stage2_crop_origin_xy[1];
  const maskWidth = data.instance_segmentation.instances[0].mask_width;
  const maskHeight = data.instance_segmentation.instances[0].mask_height;
  const maskRect = { maskX, maskY, maskWidth, maskHeight };
  console.log("loadPanoramicMasks", data, maskRect);

  for (const instance of data.instance_segmentation.instances) {
    const number = +instance.class_name;
    if (!number) continue;
    const figure = {
      instance: instance,
      isPanoFigure: true,
      number: number,
      annotationPoints: [],
    };
    let tooth = getToothByNumber(figure.number);
    if (tooth) {
      figure.tooth = tooth;
      figure.item = tooth.listItem;
    }
    figure.color = tooth ? tooth.color : "#FF0000";
    figure.panelItem = panoPanelItems.find((f) => f.id == figure.number);
    panoMaskFigures.push(figure);

    let rle = instance.mask_rle;
    let bbox = instance.bbox_xyxy;
    let box = (figure.box = {});
    box.min = { x: bbox[0], y: bbox[1] };
    box.max = { x: bbox[2], y: bbox[3] };
    box.width = bbox[2] - bbox[0];
    box.height = bbox[3] - bbox[1];
    box.center = {
      x: box.min.x + box.width / 2,
      y: box.min.y + box.height / 2,
    };
    // ctx.fillRect(box.min.x, box.min.y, box.width, box.height);
    const mask_width = instance.mask_width;
    const mask_height = instance.mask_height;
    let mask = decodeRLE(rle);
    for (let i = 0; i < mask.length; i++) {
      if (mask[i]) {
        let x = Math.floor(i / mask_height) + maskX;
        let y = (i % mask_height) + maskY;
        figure.annotationPoints.push({ x: x, y: y });
      }
    }
  }

  for (const figure of panoMaskFigures) {
    let panoShape = new Konva.Shape({
      // stroke: figure.color,
      fill: figure.color,
      strokeEnabled: false,
      opacity: 0.5,
      perfectDrawEnabled: false,
      sceneFunc: (ctx, shape) => {
        // const nativeCtx = ctx.getCanvas().getContext("2d");
        // nativeCtx.fillStyle = figure.color;
        ctx.beginPath();
        figure.annotationPoints.forEach((p) => {
          // nativeCtx.fillRect(p.x, p.y, 1, 1);
          ctx.rect(p.x, p.y, 1, 1);
        });
        // ctx.closePath();
        ctx.fillShape(shape);
      },
    });
    panoShape.number = figure.number;
    figure.panoShape = panoShape;

    panoShape.on("mouseover", function (e) {
      this.opacity(1);
      let stage = e.target.getStage();
      stage.container().style.cursor = "pointer";

      let panoFigure = getPanoFigureByShape(this);
      if (panoFigure) {
        if (panoFigure.item) panoFigure.item.isHovered = true;
        if (panoFigure.panelItem) panoFigure.panelItem.icon_color = "orange";
      }
    });

    panoShape.on("mouseout", function (e) {
      this.opacity(0.5);
      e.target.getStage().container().style.cursor = "default";
      let panoFigure = getPanoFigureByShape(this);
      if (panoFigure) {
        let toothItem = panoFigure.item;
        let isFocused = toothItem && toothItem.isFocused;
        if (!isFocused) {
          if (toothItem) toothItem.isHovered = false;
          let panelItem = panoFigure.panelItem;
          if (panelItem) {
            panelItem.icon_color = toothItem ? "blue" : "white";
          }
        }
      }
    });

    panoShape.on("dblclick", function (e) {
      ioPanoMenu.style.display = "initial";
      ioPanoMenu.style.left = e.evt.clientX + "px";
      ioPanoMenu.style.top = e.evt.clientY - 35 + "px";
      console.log("panoShape dblclick", e);

      selectedPanoFigure = getPanoFigureByShape(this);
    });
    panoKonvaLayer.add(panoShape);
    // mainShape.cache();
  }

  for (const figure of panoMaskFigures) {
    let fontSize = Math.round(imgWidth / 100);
    let text = `${figure.number}`;
    if (!figure.tooth) text += " *";
    let panoText = new Konva.Text({
      text: text,
      // fontFamily: "Calibri",
      fontSize: fontSize, //figure.box.width / 2,
      x: figure.box.center.x + maskX,
      y: figure.box.min.y - fontSize * 1.2 + maskY,
      fill: figure.color,
      // stroke: 'red',
      // strokeWidth: 2,
      shadowColor: "black",
      // shadowBlur: 0,
      shadowOffset: { x: 1, y: 2 },
      // shadowOpacity: 0.5,
    });

    figure.panoText = panoText;
    panoKonvaLayer.add(panoText);
  }
}

function getPanoFigureByShape(shape) {
  return panoMaskFigures.find((f) => f.panoShape == shape);
}

function getPanoFiguresByNumber(number) {
  return panoMaskFigures.filter((f) => f.number == number);
}

function changePanoNumber(figure, newNumber) {
  console.log("changePanoNumber", figure, newNumber);
  $("#intraoralEditNumberDialog").modal("hide");
  if (figure && figure.number != newNumber) {
    figure.number = figure.panoShape.number = newNumber;
    figure.instance.class_name = `${newNumber}`;
    figure.tooth = figure.item = null;
    let tooth = getToothByNumber(figure.number);
    if (tooth) {
      figure.tooth = tooth;
      figure.item = tooth.listItem;
    }
    figure.color = tooth ? tooth.color : "#FF0000";
    figure.panelItem = panoPanelItems.find((f) => f.id == figure.number);

    figure.panoShape.fill(figure.color);

    let text = `${figure.number}`;
    if (!figure.tooth) text += " *";
    figure.panoText.fill(figure.color);
    figure.panoText.text(text);

    panoKonvaLayer.draw();

    sendSavePanoJsonRequest(panoJsonData);
  }
}

function rleFromString(encodedString) {
  const characterCodes = encodedString
    .split("")
    .map((char) => char.charCodeAt(0));

  let codePointer = 0;
  let ith = 0;
  let count = 0;
  let more = 0;

  const counts = [];

  while (characterCodes[codePointer]) {
    count = 0;
    ith = 0;
    more = 1;

    while (more) {
      const char = characterCodes[codePointer] - 48;
      count |= (char & 0x1f) << (5 * ith);
      more = char & 0x20;
      codePointer++;
      ith++;
      if (!more && char & 0x10) count |= -1 << (5 * ith);
    }

    if (counts.length > 2) {
      count += counts[counts.length - 2];
    }
    counts.push(count);
  }

  return counts;
}

/**
 * Decode COCO RLE format to binary mask array
 * @param {Object} rle - RLE object with 'counts' and 'size' properties
 * @param {string|number[]} rle.counts - Encoded counts (either string or array)
 * @param {number[]} rle.size - [height, width] of the mask
 * @returns {Uint8Array} Flattened binary mask (values 0 or 1)
 */
function decodeRLE(rle) {
  // Parse counts (can be string or array)
  let counts;
  if (typeof rle.counts === "string") {
    counts = rleFromString(rle.counts);
  } else {
    counts = rle.counts;
  }

  const [height, width] = rle.size;
  const totalPixels = height * width;
  const mask = new Uint8Array(totalPixels);

  // Fill the mask based on RLE counts
  let pixelIndex = 0;
  let value = 0; // Start with 0 (background) or 1 depending on implementation

  for (let i = 0; i < counts.length; i++) {
    const count = counts[i];
    for (let j = 0; j < count; j++) {
      mask[pixelIndex++] = value;
    }
    value = 1 - value; // Toggle between 0 and 1
  }

  // Ensure we filled exactly the right number of pixels
  if (pixelIndex !== totalPixels) {
    console.warn(
      `RLE decoding: Expected ${totalPixels} pixels, got ${pixelIndex}`,
    );
  }

  return mask;
}

function switchShowPanoramicImg(newValue) {
  document.getElementById("ioPanoramicViewer").style.display = newValue
    ? "block"
    : "none";
  ioViewer.do_resize();
}

//#endregion

//#region Filled Teeth

async function switchFilledTeethView(newValue) {
  viewMode.isFilledTeethView = newValue;

  if (viewMode.isFilledTeethView) {
    if (!rootStubsAttempted) {
      showIntraoralLoader(`Load root stubs`, "");
      await loadRootStubGeometries(allModels);
      hideLoader();
      rootStubsAttempted = true;
    }

    if (!rootStubsLoaded) {
      viewMode.isFilledTeethView = false;
      // oldLoadFilledTeeth();
      if (viewMode.isPostprocessing) {
        swal({
          text: "Please wait for case postprocessing to complete.",
        });
      } else {
        swal({
          text: "Root stubs not found. Would you like to generate them?",
          buttons: {
            yes: { text: "Yes", value: true },
            no: { text: "No", value: false },
          },
        }).then(async (postprocess) => {
          if (postprocess) {
            console.log(`postprocess patient ${vueApp.patientId}`);
            viewMode.isPostprocessing = true;
            sendCreateStubsRequest();
          }
        });
      }
    } else {
      await showFilledTeeth();
    }
  } else {
    await hideFilledTeeth();
    // viewMode.isEdit = true;
    // switchEditMode(viewMode.isEdit);
  }
}

async function loadRootStubGeometries(inputModels) {
  let cutFilePromises = [];
  inputModels.forEach((x) => {
    let cutZipFile = x.cutZipFile;
    if (cutZipFile) {
      let elementName = x.name;
      let model = allModels.find((m) => m.name == elementName);
      let promise = cutZipFile.async("arraybuffer");
      cutFilePromises.push(promise);
      promise.then((buffer) => {
        let geometry = getGeometryFromStl(buffer);
        const tempGeo = new THREE.Geometry().fromBufferGeometry(geometry);
        tempGeo.mergeVertices();
        tempGeo.computeVertexNormals();
        geometry = new THREE.BufferGeometry().fromGeometry(tempGeo);
        model.rootStubGeometry = geometry;
      });
    }
  });
  if (cutFilePromises.length > 0) {
    await Promise.all(cutFilePromises);
    rootStubsLoaded = true;
    console.log("root stubs loaded", cutFilePromises.length);
  }
}

// !!!
async function showFilledTeeth() {
  updateSelectedTooth(null);
  teeth.forEach((model) => {
    if (model.rootStubGeometry) {
      model.mesh.geometry = model.rootStubGeometry;
    }
  });

  if (
    (upperGingiva && upperGingiva.listItem.isFocused) ||
    (lowerGingiva && lowerGingiva.listItem.isFocused)
  ) {
    await switchItemFocus(null);
  }
}

async function hideFilledTeeth() {
  teeth.forEach((model) => {
    if (model.mainGeometry) {
      model.mesh.geometry = model.mainGeometry;
    }
  });
}

//#endregion

//#region Smooth View

async function switchSmoothView(value) {
  viewMode.isSmoothView = value;
  console.log("switchSmoothView", viewMode.isSmoothView);

  if (!viewMode.isSmoothView) {
    if (!extrTeethInitialized) {
      showIntraoralLoader(`Load default teeth`, "");
      await loadExtrGeometries(allModels);
      hideLoader();
      extrTeethInitialized = true;
    }

    if (!extrTeethLoaded) {
      viewMode.isSmoothView = true;
      swal({
        text: "Extr teeth are not loaded.",
      });
    } else {
      await showExtrTeeth();
    }
  } else {
    await hideExtrTeeth();
  }
}

async function loadExtrGeometries(inputModels) {
  let extrFilePromises = [];
  inputModels.forEach((x) => {
    let extrZipFile = x.extrZipFile;
    if (extrZipFile) {
      let elementName = x.name;
      let model = allModels.find((m) => m.name == elementName);
      let promise = extrZipFile.async("arraybuffer");
      extrFilePromises.push(promise);
      promise.then((buffer) => {
        let geometry = getGeometryFromStl(buffer);
        const tempGeo = new THREE.Geometry().fromBufferGeometry(geometry);
        tempGeo.mergeVertices();
        tempGeo.computeVertexNormals();
        model.extrGeometry = new THREE.BufferGeometry().fromGeometry(tempGeo);
      });
    }
  });
  if (extrFilePromises.length > 0) {
    await Promise.all(extrFilePromises);
    extrTeethLoaded = true;
    console.log("extr teeth loaded", extrFilePromises.length);
  }
}

async function showExtrTeeth() {
  updateSelectedTooth(null);
  teeth.forEach((model) => {
    if (model.extrGeometry) {
      model.mesh.geometry = model.extrGeometry;
    }
  });

  if (
    (upperGingiva && upperGingiva.listItem.isFocused) ||
    (lowerGingiva && lowerGingiva.listItem.isFocused)
  ) {
    await switchItemFocus(null);
  }
}

async function hideExtrTeeth() {
  teeth.forEach((model) => {
    if (model.mainGeometry) {
      model.mesh.geometry = model.mainGeometry;
    }
  });
}

//#endregion

//#region Original View

async function switchOriginalView(value) {
  viewMode.isOriginalView = value;
  console.log("switchOriginalView", viewMode.isOriginalView);

  if (viewMode.isOriginalView) {
    for (const model of gingivae) {
      if (!model.originalMesh) {
        await loadOriginalMesh(model);
        if (!originalBox) {
          originalBox = model.originalMesh.defaultBox;
        } else {
          originalBox = combineBoxes(
            originalBox,
            model.originalMesh.defaultBox,
          );
        }
      }
    }

    if (!originalZoom) {
      let padding = 10;
      let w = (originalBox.size.x + padding) / 2;
      let h = (originalBox.size.y + padding) / 2;
      let fovX = ioViewer.camera.fov * ioViewer.camera.aspect;
      let fovY = ioViewer.camera.fov;
      let distanceX = w / Math.tan((Math.PI * fovX) / 360) + w;
      let distanceY = h / Math.tan((Math.PI * fovY) / 360) + w;
      originalZoom = Math.max(distanceX, distanceY);
      originalCenter = originalBox.center;
      console.log("originalCenter", originalCenter);
      // drawPoint(originalCenter, ioViewer.scene, 0xff0000, 2);
    }

    lookAtOriginal();

    allModels.forEach((model) => {
      model.mesh.visible = false;
      if (model.listItem) model.listItem.isHidden = true;
      // let item = model.listItem;
    });
    gingivae.forEach((model) => {
      if (model.originalMesh) {
        model.originalMesh.visible =
          model.isUpper == viewMode.isUpper || viewMode.isFrontView;
      }
    });
  } else {
    allModels.forEach((model) => {
      if (model.listItem) model.listItem.isHidden = false;
    });

    gingivae.forEach((model) => {
      if (model.originalMesh) {
        model.originalMesh.visible = false;
      }
    });
    if (viewMode.isFrontView) {
      await switchViewModes(true, false);
      viewMode.isUpper = viewMode.isLower = viewMode.isEdit = false;
      await switchViewModes(true, true);
    } else {
      await switchViewModes(viewMode.isUpper, viewMode.isLower);
    }
  }
}

function lookAtOriginal() {
  ioViewer.camera.lookAt(originalCenter);
  ioViewer.camera.up.y = 1;
  ioViewer.camera.up.x = 0;
  ioViewer.camera.up.z = 0;
  ioViewer.camera.position.x = 0; //point.x;
  ioViewer.camera.position.y = 0; //point.y;
  ioViewer.camera.position.z = originalZoom;
  ioViewer.controls.target = new THREE.Vector3(
    originalCenter.x,
    originalCenter.y,
    originalCenter.z,
  );
  ioViewer.camera.updateProjectionMatrix();
  ioViewer.controls.update();
}

async function loadOriginalMesh(model) {
  showIntraoralLoader();

  try {
    let url = model.isUpper
      ? `${vueApp.dicomUrl}/case/upper_${vueApp.patientId}.STL`
      : `${vueApp.dicomUrl}/case/lower_${vueApp.patientId}.STL`;
    // console.log("loadOriginalMesh", url);
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    let geometry = getGeometryFromStl(arrayBuffer);
    // const tempGeo = new THREE.Geometry().fromBufferGeometry(geometry);
    // tempGeo.mergeVertices();
    // tempGeo.computeVertexNormals();
    // geometry = new THREE.BufferGeometry().fromGeometry(tempGeo);
    geometry.computeBoundingBox();

    model.originalMesh = new THREE.Mesh(geometry, model.mesh.material);
    model.originalMesh.defaultBox = offsetBox(geometry.boundingBox);
    model.originalMesh.visible = false;
    ioViewer.scene.add(model.originalMesh);
  } catch (ex) {}

  hideLoader();
}

function combineBoxes(box1, box2) {
  let newMin = new THREE.Vector3(
    Math.min(box1.min.x, box2.min.x),
    Math.min(box1.min.y, box2.min.y),
    Math.min(box1.min.z, box2.min.z),
  );
  let newMax = new THREE.Vector3(
    Math.max(box1.max.x, box2.max.x),
    Math.max(box1.max.y, box2.max.y),
    Math.max(box1.max.z, box2.max.z),
  );

  let newBox = new THREE.Box3(newMin, newMax);
  newBox = offsetBox(newBox);
  return newBox;
}

//#endregion

//#region Save/Reset

async function saveIntraoralData(
  updateExportedData,
  allChanged,
  updateFilledTeeth,
  updateMeasurements,
) {
  console.log("saveIntraoralData", updateExportedData, allChanged);
  viewMode.isSaving = true;

  if (selectedTooth && viewMode.isBrushMode && isEditMode) {
    await completeBrush(true);
  }
  let upperPointsData = {};
  let lowerPointsData = {};
  let upperAnchorsData = {};
  let lowerAnchorsData = {};
  let settingsData = {
    needPostprocess: !!updateExportedData,
    updateFilledTeeth: updateFilledTeeth,
    updateMeasurements: updateMeasurements,
    teeth: {},
  };
  var zip = new JSZip();
  teeth.forEach((tooth) => {
    settingsData.teeth[tooth.name] = {
      name: tooth.name,
      color: tooth.color,
      baseNumber: tooth.baseNumber,
      number: tooth.number,
      glbNumber: tooth.glbNumber,
      isSaved: true,
      isUpper: tooth.isUpper,
      isAdded: tooth.isAdded,
      isDeleted: tooth.isDeleted,
      isChanged: tooth.isChanged || allChanged,
      isBrushChanged: !!tooth.isBrushChanged,
      crossedTeeth: tooth.crossedTeeth,
    };
    if (tooth.isChanged) {
      console.log("changed tooth: ", tooth.name);
    }
    tooth.baseNumber = tooth.number;
    tooth.recentlyAdded = tooth.recentlyDeleted = false;
    setToothIsChanged(tooth, false);
    if (tooth.isDeleted) return;

    let stlBlob = exportGeometryToStlBlob(tooth.mainGeometry);
    tooth.savedGeometry = tooth.mainGeometry;
    tooth.savedPoints = getSavedBorderPointsArr(tooth);
    // tooth.savedAnchorPoints = getSavedAnchorPointsArr(tooth);
    tooth.savedAnchorGroup = getSavedAnchorGroup(tooth);

    zip.file(GEO_TAG + tooth.name + ".stl", stlBlob);

    if (tooth.isUpper) {
      upperPointsData[tooth.name] = tooth.savedPoints;
      if (tooth.savedAnchorGroup && tooth.savedAnchorGroup.length) {
        for (let i = 0; i < tooth.savedAnchorGroup.length; i++) {
          const borderGroup = tooth.savedAnchorGroup[i];
          let tname = i > 0 ? `${tooth.name}-${i}` : tooth.name;
          upperAnchorsData[tname] = borderGroup;
        }
      }
    } else {
      lowerPointsData[tooth.name] = tooth.savedPoints;
      if (tooth.savedAnchorGroup && tooth.savedAnchorGroup.length) {
        for (let i = 0; i < tooth.savedAnchorGroup.length; i++) {
          const borderGroup = tooth.savedAnchorGroup[i];
          let tname = i > 0 ? `${tooth.name}-${i}` : tooth.name;
          lowerAnchorsData[tname] = borderGroup;
        }
      }
    }
  });
  let settingsString = JSON.stringify(settingsData);
  console.log(settingsString);
  zip.file("intraoral_settings.json", settingsString);

  if (upperMesh) {
    zip.file("saved_upper_border_points.json", JSON.stringify(upperPointsData));
    zip.file("saved_upper_extr_anchors.json", JSON.stringify(upperAnchorsData));
  }

  if (lowerMesh) {
    zip.file("saved_lower_border_points.json", JSON.stringify(lowerPointsData));
    zip.file("saved_lower_extr_anchors.json", JSON.stringify(lowerAnchorsData));
  }

  console.log("saveIntraoralData result", zip);

  zip.generateAsync({ type: "blob" }).then(async function (blob_data) {
    await saveMeasurements(upper_measurements, true);
    await saveMeasurements(lower_measurements, false);

    await sendSaveIntraoralDataRequest(
      blob_data,
      upperMesh && lowerMesh,
      rootStubsLoaded,
      (zipBlob) => {
        document.body.style.cursor = "default";
        applyZipBlob(zipBlob);
      },
    );
    viewMode.isSaving = false;
    postprocessCheck();
  });
}

async function applyZipBlob(zipBlob) {
  document.body.style.cursor = "default";
  if (!zipBlob) {
    return;
  }

  try {
    let tempTooth = selectedTooth;
    if (viewMode.isEdit && tempTooth) {
      updateSelectedTooth(null);
    }

    let zip = await JSZip.loadAsync(zipBlob);
    let errorTeeth = [];
    let zipKeys = Object.keys(zip.files);
    console.log("applyZipBlob", zipKeys);
    for (const fileName of zipKeys) {
      showIntraoralLoader();
      await sleep(100);
      let zipFile = zip.file(fileName);
      let model = getModelByFileName(fileName);
      if (model) {
        try {
          let buffer = await zipFile.async("arraybuffer"); //.then((buffer) => {
          let elementGeometry = getGeometryFromStl(buffer); //, (elementGeometry) => {
          const tempGeo = new THREE.Geometry().fromBufferGeometry(
            elementGeometry,
          );
          tempGeo.mergeVertices();
          tempGeo.computeVertexNormals();
          elementGeometry = new THREE.BufferGeometry().fromGeometry(tempGeo);
          elementGeometry.computeBoundingBox();

          let box = elementGeometry.boundingBox;
          box = offsetBox(box, 0, 0, 0);
          model.defaultBox = box;
          model.defaultPosition = {
            x: 0, // box.center.x,
            y: 0, // box.center.y,
            z: 0, // box.center.z,
          };

          let geometry = model.isGingiva
            ? elementGeometry
            : prepareGeometry(elementGeometry, model.isGingiva);
          // let change = {
          //   model: selectedTooth,
          //   oldGeometry: tooth.mesh.geometry.clone(),
          //   oldPoints: allPoints,
          //   newGeometry: geometry.clone(),
          //   newPoints: allPoints,
          // };
          // addIoChange(change);
          // if (currentIoChange) {
          //   currentIoChange.newGeometry = geometry.clone();
          // }
          let mesh = model.isGingiva ? model.filledMesh : model.mesh;
          mesh.geometry.dispose();
          mesh.geometry = model.mainGeometry = geometry;
          mesh.verticesMap = mesh.indexMap = null;
          model.savedGeometry = model.mainGeometry = geometry;
          if (model.isGingiva) {
            if (model.isUpper) {
              upperMesh = createGlobalMesh(upperMeshes, upperGingiva);
              upperBox = offsetBox(upperMesh.geometry.boundingBox);
              upperCameraCenter = new THREE.Vector3(
                upperBox.center.x,
                upperBox.center.y,
                upperBox.center.z,
              );
              upperGraph = null;
            } else {
              lowerMesh = createGlobalMesh(lowerMeshes, lowerGingiva);
              lowerBox = offsetBox(lowerMesh.geometry.boundingBox);
              lowerCameraCenter = new THREE.Vector3(
                lowerBox.center.x,
                lowerBox.center.y,
                lowerBox.center.z,
              );
              lowerGraph = null;
            }
            model.filledMesh.closedGeometry = model.filledMesh.geometry;
            await sleep(100);
            requestLoadEditData();
          } else {
            if (model.pointsGroup) {
              ioViewer.scene.remove(model.pointsGroup);
              model.pointsGroup = null;
            }
          }
          if (model.isTooth && model.mesh.geometry.isSmallArea) {
            errorTeeth.push(model);
          }
        } catch (error) {
          console.log("applyZipBlob tooth ERROR", fileName, error);
          errorTeeth.push(model);
          model.mesh.geometry.isSmallArea = true;
          viewMode.isBrushDraw = true;
        }
      } else {
        if (fileName.endsWith(".json")) {
          let text = await zipFile.async("string");
          let jsonObj = JSON.parse(text);
          if (fileName == "saved_upper_border_points.json")
            applyBorderPoints(jsonObj, false, true);
          if (fileName == "saved_lower_border_points.json")
            applyBorderPoints(jsonObj, false, false);
        }
      }
    }

    if (viewMode.isEdit && tempTooth) {
      updateSelectedTooth(tempTooth);
    }

    if (errorTeeth.length > 0) {
      let teethNumbers = errorTeeth.map((t) => t.number).join(", ");
      let swalText =
        errorTeeth.length == 1
          ? `Segmentation issue detected on tooth ${errorTeeth[0].number}. Please use the paint brush segmentation tool to annotate this tooth.`
          : `Segmentation issue detected on teeth ${teethNumbers}. Please use the paint brush segmentation tool to annotate these teeth.`;
      let willBrush = await swal({
        text: swalText,
        // buttons: {
        //   yes: { text: "OK", value: true },
        //   no: { text: "Cancel", value: false },
        // },
      });
      if (viewMode.isEdit) {
        switchToBrushMode(true);
      }
    }
  } catch (error) {
    console.log("applyZipBlob ERROR", error);
  }
  hideLoader();
}

async function resetIoView(toMainGeometries) {
  await unfocusAny();
  // updateSelectedTooth(null);
  intraoralMenu.style.display = "none";
  dragPoint = null;
  intersectedTooth = null;
  ioViewer.controls.enabled = true;

  if (viewMode.isEdit) {
    await switchEditMode(false);
  }

  if (viewMode.isMeasureMode) {
    switchMeasureMode(false);
  }
  if (viewMode.isArchMode) {
    switchArchMode(false);
  }
  if (viewMode.isPanoramicRoots) {
    switchPanoramicRoots(false);
  }
  if (viewMode.showTeethNumbers) {
    switchTeethNumbers(false);
  }
  if (viewMode.isOriginalView) {
    await switchOriginalView(false);
  }

  if (toMainGeometries) {
    if (viewMode.isFilledTeethView) {
      switchFilledTeethView(false);
    }
    if (viewMode.hasSmoothView && !viewMode.isSmoothView) {
      await switchSmoothView(true);
    }
    // if (viewMode.isFrontView) {
    //   viewMode.isUpper = true;
    //   viewMode.isLower = viewMode.isFrontView = false;
    //   switchViewModes(true, false);
    // }
  }
}

function globalResetIntraoral() {
  resetIoView();
  clearTempMeshes(ioViewer.scene);

  teeth.forEach((model) => {
    let item = model.listItem;
    if (model.glbNumber && model.number != model.glbNumber) {
      let glbName = `tooth_${model.glbNumber}`;
      model.name = item.name = item.displayName = glbName;
      model.number = item.number = model.glbNumber;
      if (model.mesh && viewMode.showTeethNumbers) showToothNumber(model);
    }

    let vmesh = model.mesh;
    if (model.isDeleted && !model.isAdded) {
      model.isDeleted = item.isDeleted = false;
      model.recentlyAdded = true;
      vmesh.visible = true;
    }

    if (model.isAdded) {
      model.isDeleted = model.recentlyDeleted = item.isDeleted = true;
      vmesh.visible = false;

      var mesh = model.mainMesh;
      if (mesh && mesh.numberGroup) {
        ioViewer.scene.remove(mesh.numberGroup);
        mesh.numberGroup = null;
      }
    }

    if (vmesh.visible && model.baseGeometry) {
      let cloneGeometry = model.baseGeometry.clone();
      cloneGeometry.computeBoundingBox();

      let box = cloneGeometry.boundingBox;
      box = offsetBox(box, 0, 0, globalDz);
      // vmesh.position.set(box.center.x, box.center.y, box.center.z);

      let geometry = prepareGeometry(cloneGeometry, model.isGingiva);
      vmesh.geometry.dispose();
      vmesh.geometry = model.mainGeometry = geometry;
    }

    if (model.pointsGroup) {
      ioViewer.scene.remove(model.pointsGroup);
      model.pointsGroup = null;
    }

    if (model.basePoints) {
      model.points = model.basePoints;
      model.anchorPoints = model.baseAnchorPoints;
      model.anchorGroup = model.baseAnchorGroup;
      if (model == selectedTooth && !model.isDeleted) {
        document.getElementById(
          "selectedToothHint",
        ).innerHTML = `"${model.name}"`;
        createPointsGroup(model);
        model.pointsGroup.visible = !item.isFocused;
      }
    }
  });

  clearIoChanges();
  // selectedTooth = null;
}

function resetIntraoral() {
  resetIoView();
  clearTempMeshes(ioViewer.scene);

  // resetIoCamera();
  teeth.forEach((model) => {
    let item = model.listItem;
    if (model.baseNumber && model.number != model.baseNumber) {
      let baseName = `tooth_${model.baseNumber}`;
      model.name = item.name = item.displayName = baseName;
      model.number = item.number = model.baseNumber;
      if (model.mesh && viewMode.showTeethNumbers) showToothNumber(model);
    }

    let vmesh = model.mesh;
    if (model.recentlyDeleted) {
      model.isDeleted = model.recentlyDeleted = item.isDeleted = false;
      vmesh.visible = true;
    }

    if (model.recentlyAdded) {
      model.isDeleted = item.isDeleted = true;
      vmesh.visible = false;
      var mesh = model.mainMesh;
      if (mesh && mesh.numberGroup) {
        ioViewer.scene.remove(mesh.numberGroup);
        mesh.numberGroup = null;
      }
    }

    if (model.savedGeometry && vmesh.visible) {
      let cloneGeometry = model.savedGeometry; //.clone();
      vmesh.geometry.dispose();
      vmesh.geometry = model.mainGeometry = cloneGeometry;
    }

    if (model.pointsGroup) {
      ioViewer.scene.remove(model.pointsGroup);
      model.pointsGroup = null;
    }

    let sPoints = model.savedPoints ? model.savedPoints : model.basePoints;
    let aPoints = model.savedAnchorPoints
      ? model.savedAnchorPoints
      : model.baseAnchorPoints;
    let anchorGroup = model.savedAnchorGroup
      ? model.savedAnchorGroup
      : model.anchorGroup;
    if (sPoints) {
      model.points = sPoints;
      model.anchorPoints = aPoints;
      model.anchorGroup = anchorGroup;
      if (model == selectedTooth && !model.isDeleted) {
        createPointsGroup(model);
        model.pointsGroup.visible = !item.isFocused;
        updateSelectedTooth(model);
      }
    }
  });
  clearIoChanges();
  // selectedTooth = null;
}

//#endregion

//#region Undo/Redo

function addIoChange(change) {
  if (currentIoChange) {
    let length = ioChanges.length;
    let index = ioChanges.indexOf(currentIoChange) + 1;
    let deletedChanges = ioChanges.splice(index, length - index);
    deletedChanges.forEach((c) => {
      c.oldGeometry.dispose();
      c.newGeometry.dispose();
    });
  } else {
    clearIoChanges();
  }
  ioChanges.push(change);
  currentIoChange = change;
}

function applyIoChange(change, applyNew) {
  if (!change) return;

  let model = change.model;
  if (model && !model.isDeleted) {
    setToothIsChanged(model, true);
    let vmesh = model.mesh;
    let cloneGeometry = applyNew ? change.newGeometry : change.oldGeometry;
    vmesh.geometry = model.mainGeometry = cloneGeometry;
    vmesh.verticesMap = vmesh.indexMap = null;
    if (model.pointsGroup) {
      ioViewer.scene.remove(model.pointsGroup);
      model.pointsGroup = null;
    }

    model.points = applyNew ? change.newPoints : change.oldPoints;
    model.anchorPoints = applyNew
      ? change.newAnchorPoints
      : change.oldAnchorPoints;
    model.anchorGroup = applyNew
      ? change.newAnchorGroup
      : change.oldAnchorGroup;
    if (
      selectedTooth != null &&
      selectedTooth != model &&
      selectedTooth.pointsGroup
    ) {
      selectedTooth.pointsGroup.visible = false;
    }
    selectedTooth = model;
    createPointsGroup(model);
    model.pointsGroup.visible = !model.listItem.isFocused;
    updateSelectedTooth(model);
  }
}

function intraoralUndo() {
  if (currentIoChange) {
    let index = ioChanges.indexOf(currentIoChange);
    // console.log("intraoralUndo", index, currentIoChange);
    applyIoChange(currentIoChange, false);
    currentIoChange = ioChanges[index - 1];
  }
}

function intraoralRedo() {
  let index = ioChanges.indexOf(currentIoChange) + 1;
  let nextIoChange = ioChanges[index];
  // console.log("intraoralRedo", index, nextIoChange);
  if (nextIoChange) {
    applyIoChange(nextIoChange, true);
    currentIoChange = nextIoChange;
  }
}

function clearIoChanges() {
  ioChanges.forEach((change) => {
    change.oldGeometry.dispose();
    change.newGeometry.dispose();
  });

  ioChanges = [];
}

//#endregion

//#region Postprocess

const postprocessInterval = 60000;
let postprocessVar = null;

function startPostprocessWatch() {
  postprocessCheck();
  postprocessVar = setInterval(postprocessCheck, postprocessInterval);
}

function stopPostprocessWatch() {
  clearInterval(postprocessVar);
}

async function postprocessCheck() {
  let isPostprocessing = await checkPatientInPostprocess(vueApp.patientId);
  if (viewMode.isPostprocessing && !isPostprocessing) {
    console.log("postprocessCheck update");
    // let zipUrl = vueApp.intraoralZipUrl;
    // let intraoralZip = await fetchZipAsync(zipUrl);
    if (upper_measurements) {
      // await loadMeasurementsFromZip(
      //   intraoralZip,
      //   "upper_measurements.json",
      //   true,
      // );
      // await saveMeasurements(upper_measurements, true);
      let upperUrl = `${vueApp.stlBaseUrl}/saved_upper_measurements.json`;
      let jsonObj = await fetchJsonAsync(upperUrl);
      if (jsonObj) applyMeasurements(jsonObj, false, true);
    }

    if (lower_measurements) {
      // await loadMeasurementsFromZip(
      //   intraoralZip,
      //   "lower_measurements.json",
      //   false,
      // );
      // await saveMeasurements(lower_measurements, false);
      let lowerUrl = `${vueApp.stlBaseUrl}/saved_lower_measurements.json`;
      let jsonObj = await fetchJsonAsync(lowerUrl);
      if (jsonObj) applyMeasurements(jsonObj, false, false);
    }
    await switchMeasureMode(viewMode.isMeasureMode);
    // TODO update stubs
  }
  // console.log("postprocessCheck", isPostprocessing);
  viewMode.isPostprocessing = isPostprocessing;
}

//#endregion

//#region Export

export {
  GEO_TAG,
  EXTR_TAG,
  CUT_TAG,
  SMOOTHED_TAG,
  TOOTH_TAG,
  STL_TAG,
  ioViewer,
  ioPlane,
  ioRaycaster,
  allModels,
  gingivae,
  upperBox,
  lowerBox,
  completeBrush,
  cancelBrush,
  getIoMouse3dDirection,
  checkIsEditMode,
  switchFilledTeethView,
  switchShowPanoramicImg,
  fillIntraoralList,
  addClick,
  changeItemVisibility,
  deleteItem,
  editItemNumber,
  highlightItem,
  unhighlightItem,
  unfocusAny,
  switchItemFocus,
  selectItem,
  randomColorValue,
  monochromeColorValue,
  greyColorValue,
  changeTeethColor,
  saveIntraoralData,
  resetIntraoral,
  globalResetIntraoral,
  switchEditMode,
  enterIntraoralOrtho,
  showIoViewer,
  setAllModels,
  initStlViewer,
  applyBorderPoints,
  applyAnchorPoints,
  switchViewModes,
  cancelAdd,
  resetIoCamera,
  rotateIntraoralToView,
  setRotateCenter,
  smoothSelectedToothBorder,
  repairIntraoralColouring,
  intraoralUndo,
  intraoralRedo,
  updateOpacity,
  getModelByName,
  startAddingTooth,
  switchTeethNumbers,
  changeToothNumber,
  startPostprocessWatch,
  stopPostprocessWatch,
  postprocessCheck,
  viewMode,
  switchToContourMode,
  switchToBrushMode,
  switchBrushDrawMode,
  updateBrushMesh,
  getModelByItem,
  setToothIsChanged,
  removeBraces,
  getGlobalMesh,
  updateRaycasterFromEvent,
  applyMeasurements,
  upper_measurements,
  lower_measurements,
  switchPanoramicRoots,
  resetIoView,
  switchItemOpen,
  changePanoNumber,
  getPanoFiguresByNumber,
  switchOriginalView,
  switchSmoothView,
  intersectTooth,
  defaultIoZoom,
  defaultIoZoom2,
  defaultIoBox,
  sceneBackgroundColor,
  intraoralMenu,
  ioPanoMenu
};
//#endregion
